import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
  getDoc,
  doc,
  orderBy,
  limit,
  Timestamp,
  startAfter,
  endBefore,
  onSnapshot
} from 'firebase/firestore';
import { FILTER_PERIODS } from '../constants';
import { getCachedCollectionData, setCachedCollectionData } from './analyticsDataCache';
import {
  processUserDataForAnalytics,
  segmentUsersByEngagement,
  segmentUsersByLifecycle,
  segmentUsersByFeatureUsage,
  segmentUsersByValue
} from './userDataProcessor';
import { firebaseLogger } from '../utils/logger';
import { subscribeToCollection, subscribeToDateRange } from './realtimeService';
import { trackQuery } from './performanceMonitoring';

/**
 * Firebase Service for Admin Dashboard
 * Optimized queries with proper indexing and caching strategies
 */

/**
 * Get date range for a given period
 * @param {string} period - Time period ('day', 'week', 'month', 'year', 'all')
 * @returns {Object} - Object with start, end (Timestamps) and startDate, endDate (Date objects)
 */
const getDateRange = (period) => {
  const now = new Date();
  const start = new Date();
  
  switch (period) {
    case FILTER_PERIODS.DAY:
      start.setDate(now.getDate() - 1);
      break;
    case FILTER_PERIODS.WEEK:
      start.setDate(now.getDate() - 7);
      break;
    case FILTER_PERIODS.MONTH:
      start.setMonth(now.getMonth() - 1);
      break;
    case FILTER_PERIODS.YEAR: {
      // Start of current year (January 1st)
      start.setMonth(0);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      break;
    }
    case FILTER_PERIODS.ALL: {
      // Very early date to get all data (e.g., year 2000)
      const allTimeStart = new Date('2000-01-01T00:00:00Z');
      return {
        start: Timestamp.fromDate(allTimeStart),
        end: Timestamp.fromDate(now),
        startDate: allTimeStart,
        endDate: now,
        isAllTime: true // Flag to indicate all time query
      };
    }
    case 'prevDay': {
      const prevEnd = new Date();
      prevEnd.setDate(now.getDate() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevEnd.getDate() - 1);
      return {
        start: Timestamp.fromDate(prevStart),
        end: Timestamp.fromDate(prevEnd),
        startDate: prevStart,
        endDate: prevEnd
      };
    }
    case 'prevWeek': {
      const prevEnd = new Date();
      prevEnd.setDate(now.getDate() - 7);
      const prevStart = new Date(prevEnd);
      prevStart.setDate(prevEnd.getDate() - 7);
      return {
        start: Timestamp.fromDate(prevStart),
        end: Timestamp.fromDate(prevEnd),
        startDate: prevStart,
        endDate: prevEnd
      };
    }
    case 'prevMonth': {
      const prevEnd = new Date();
      prevEnd.setMonth(now.getMonth() - 1);
      const prevStart = new Date(prevEnd);
      prevStart.setMonth(prevEnd.getMonth() - 1);
      return {
        start: Timestamp.fromDate(prevStart),
        end: Timestamp.fromDate(prevEnd),
        startDate: prevStart,
        endDate: prevEnd
      };
    }
    default:
      // Default to day if unknown period
      start.setDate(now.getDate() - 1);
  }
  
  // Reset time to start/end of day for better accuracy
  if (period !== FILTER_PERIODS.ALL && period !== FILTER_PERIODS.YEAR) {
    start.setHours(0, 0, 0, 0);
  }
  now.setHours(23, 59, 59, 999);
  
  return {
    start: Timestamp.fromDate(start),
    end: Timestamp.fromDate(now),
    startDate: start,
    endDate: now
  };
};

/**
 * Parse date string from database
 * Handles various date formats from Firebase
 * @param {string|Date|Timestamp} dateString - Date to parse
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export const parseDateString = (dateString, context = 'unknown') => {
  if (!dateString) return null;
  
  // Handle Firestore Timestamp objects
  if (dateString && typeof dateString.toDate === 'function') {
    try {
    return dateString.toDate();
    } catch (error) {
      firebaseLogger.warn('Failed to parse Firestore Timestamp', { context, error });
      return null;
    }
  }
  
  // Handle Timestamp objects with seconds property
  if (dateString && typeof dateString === 'object' && (dateString.seconds || dateString._seconds)) {
    try {
      const sec = dateString.seconds ?? dateString._seconds;
      return new Date(sec * 1000);
    } catch (error) {
      firebaseLogger.warn('Failed to parse Timestamp object', { context, error });
      return null;
    }
  }
  
  // Handle Date objects
  if (dateString instanceof Date) {
    if (isNaN(dateString.getTime())) {
      firebaseLogger.warn('Invalid Date object', { context, dateString });
      return null;
    }
    return dateString;
  }
  
  // Handle numbers (epoch milliseconds or seconds)
  if (typeof dateString === 'number') {
    if (dateString > 1000000000) {
      // Likely epoch seconds or milliseconds
      const ms = dateString < 2000000000 ? dateString * 1000 : dateString;
      const date = new Date(ms);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    firebaseLogger.warn('Invalid date number', { context, dateString });
    return null;
  }
  
  // Handle different date string formats
  if (typeof dateString === 'string') {
    // Handle ISO format (most common)
    if (dateString.includes('T') || dateString.includes('Z') || /^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    // Handle "28 octobre 2025 à 17:17:56 UTC+1" format
    if (dateString.includes('octobre') || dateString.includes('UTC') || dateString.includes('à')) {
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    
    // Try parsing as-is
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
    
    // Log unparseable date strings for debugging
    firebaseLogger.debug('Could not parse date string', { context, dateString });
  }
  
  return null;
};

/**
 * Check if a date falls within the specified period
 * @param {string|Date|Timestamp} dateString - Date to check
 * @param {Date} startDate - Period start date
 * @param {Date} endDate - Period end date
 * @returns {boolean} - True if date is within period
 */
const isDateInPeriod = (dateString, startDate, endDate, context = 'unknown') => {
  const date = parseDateString(dateString, context);
  if (!date) {
    // Log when dates can't be parsed (but don't spam - use debug level)
    firebaseLogger.debug('Date could not be parsed for period check', { context, dateString });
    return false;
  }
  const inPeriod = date >= startDate && date <= endDate;
  return inPeriod;
};

/**
 * Check if a user is a test/development user that should be excluded from analytics
 * Filters out users with "test", "dev", or "prod" in their name (case-insensitive)
 * Also excludes specific users: WeZplit, Haxxolotto, PauluneMoon, and all dev accounts
 * @param {Object} userData - User data object
 * @returns {boolean} - True if user should be excluded (is a test user)
 */
export const isTestUser = (userData) => {
  if (!userData) return false;
  
  const name = (userData.name || '').toLowerCase();
  const email = ((userData.primary_email || userData.email || '')).toLowerCase();
  
  // Exclude specific users by name or email
  const excludedUsers = [
    'wezplit',
    'haxxolotto',
    'paulunemoon'
  ];
  
  for (const excludedUser of excludedUsers) {
    if (name === excludedUser || name.includes(excludedUser) || 
        email.includes(excludedUser)) {
    return true;
    }
  }
  
  // Check name for test/dev/prod keywords (catches all dev accounts)
  if (name.includes('test') || name.includes('dev') || name.includes('prod') ||
      name.includes('developer') || name.includes('development')) {
    return true;
  }
  
  // Also check email for test/dev/prod keywords
  if (email.includes('test') || email.includes('dev') || email.includes('prod') ||
      email.includes('developer') || email.includes('development')) {
    return true;
  }
  
  return false;
};

/**
 * Check if an email belongs to a test user (for filtering waitlist entries, etc.)
 * @param {string} email - Email address to check
 * @returns {boolean} - True if email belongs to a test user
 */
const isTestUserEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const emailLower = email.toLowerCase().trim();
  return isTestUser({ email: emailLower, name: emailLower });
};

/**
 * Check if a transaction is on devnet (should be excluded from analytics)
 * @param {Object} txData - Transaction data object
 * @returns {boolean} - True if transaction is on devnet
 */
export const isDevnetTransaction = (txData) => {
  if (!txData) return false;
  
  // Check various network field names
  const network = (
    txData.blockchain_network || 
    txData.network || 
    txData.networkName || 
    txData.chain || 
    txData.chain_name || 
    txData.chainId ||
    txData.network_id ||
    ''
  ).toLowerCase();
  
  // Check for devnet indicators
  const devnetIndicators = [
    'devnet',
    'dev-net',
    'testnet',
    'test-net',
    'local',
    'localhost'
  ];
  
  // Check if network contains any devnet indicator
  for (const indicator of devnetIndicators) {
    if (network.includes(indicator)) {
      return true;
    }
  }
  
  // Check chain ID for devnet values (common devnet chain IDs)
  const chainId = txData.chainId || txData.chain_id || txData.network_id;
  if (chainId) {
    const chainIdStr = String(chainId).toLowerCase();
    // Common devnet/testnet chain IDs
    if (chainIdStr.includes('devnet') || 
        chainIdStr.includes('testnet') ||
        chainIdStr === '103' || // Solana devnet
        chainIdStr === '111' || // Ethereum testnet (Ropsten)
        chainIdStr === '3' ||   // Ethereum testnet (Ropsten)
        chainIdStr === '4') {   // Ethereum testnet (Rinkeby)
      return true;
    }
  }
  
  return false;
};

/**
 * Cache of test user IDs to avoid repeated Firebase queries
 * @type {Map<string, boolean>}
 */
const testUserCache = new Map();

/**
 * Check if a user ID belongs to a test user
 * Fetches user data from Firebase if not in cache
 * @param {string} userId - User ID to check
 * @returns {Promise<boolean>} - True if user is a test user
 */
const isTestUserId = async (userId) => {
  if (!userId) return false;
  
  // Check cache first
  if (testUserCache.has(userId)) {
    return testUserCache.get(userId);
  }
  
  try {
    // Fetch user data from Firebase
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      testUserCache.set(userId, false);
      return false;
    }
    
    const userData = userDoc.data();
    const isTest = isTestUser(userData);
    testUserCache.set(userId, isTest);
    return isTest;
  } catch (error) {
    firebaseLogger.warn('Error checking user ID', { userId, error });
    // On error, assume not a test user to avoid blocking legitimate data
    testUserCache.set(userId, false);
    return false;
  }
};

/**
 * Batch check multiple user IDs for test users
 * More efficient than checking one at a time
 * @param {string[]} userIds - Array of user IDs to check
 * @returns {Promise<Map<string, boolean>>} - Map of userId -> isTestUser
 */
export const batchCheckTestUsers = async (userIds) => {
  const result = new Map();
  const uncachedIds = [];
  
  // Check cache first
  userIds.forEach(userId => {
    if (testUserCache.has(userId)) {
      result.set(userId, testUserCache.get(userId));
    } else if (userId) {
      uncachedIds.push(userId);
    }
  });
  
  if (uncachedIds.length === 0) {
    return result;
  }
  
  try {
    // Fetch all uncached users in batch
    const userDocs = await Promise.all(
      uncachedIds.map(userId => getDoc(doc(db, 'users', userId)).catch(() => null))
    );
    
    userDocs.forEach((userDoc, index) => {
      const userId = uncachedIds[index];
      if (userDoc && userDoc.exists()) {
        const userData = userDoc.data();
        const isTest = isTestUser(userData);
        result.set(userId, isTest);
        testUserCache.set(userId, isTest);
      } else {
        result.set(userId, false);
        testUserCache.set(userId, false);
      }
    });
  } catch (error) {
    firebaseLogger.warn('Error batch checking users', { error });
    // On error, assume not test users
    uncachedIds.forEach(userId => {
      result.set(userId, false);
      testUserCache.set(userId, false);
    });
  }
  
  return result;
};

/**
 * Check if an email has admin access to the dashboard
 * Only users that are excluded from analytics (test/dev/prod users and PauluneMoon) can access admin
 * @param {string} email - Email address to check
 * @returns {boolean} - True if email has admin access
 */
export const hasAdminAccess = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }
  
  const emailLower = email.toLowerCase();
  
  // Allow specific users (PauluneMoon)
  if (emailLower.includes('paulunemoon')) {
    return true;
  }
  
  // Allow test/dev/prod users
  if (emailLower.includes('test') || emailLower.includes('dev') || emailLower.includes('prod')) {
    return true;
  }
  
  return false;
};

/**
 * Verify admin access by checking email and optionally fetching user data from Firebase
 * If email doesn't match criteria directly, checks Firebase to see if user is a test user
 * @param {string} email - Email address to verify
 * @param {boolean} verifyInFirebase - Whether to verify user exists in Firebase (default: true)
 * @returns {Promise<Object>} - { hasAccess: boolean, userData?: Object, error?: string }
 */
export const verifyAdminAccess = async (email, verifyInFirebase = true) => {
  try {
    const emailLower = (email || '').toLowerCase().trim();
    firebaseLogger.debug('Verifying access for email', { email: emailLower });
    
    // First check if email matches admin criteria directly
    const emailHasAccess = hasAdminAccess(emailLower);
      firebaseLogger.debug('Email has direct access', { email: emailLower, hasAccess: emailHasAccess });
    
    if (emailHasAccess) {
      // Email matches, but still verify in Firebase if requested to get user data
      if (verifyInFirebase) {
        try {
          firebaseLogger.debug('Verifying email in Firebase');
          const userData = await fetchUserByEmail(emailLower);
          if (userData) {
            firebaseLogger.info('Access granted - email matches and user found in Firebase');
            return { hasAccess: true, userData };
          } else {
            firebaseLogger.info('User not found in Firebase, but email matches criteria - allowing access');
          }
        } catch (firebaseError) {
          // If Firebase query fails but email matches, still allow access
          firebaseLogger.warn('Firebase verification failed, allowing access based on email check', { error: firebaseError });
        }
      }
      firebaseLogger.info('Access granted - email matches admin criteria');
      return { hasAccess: true };
    }
    
    // Email doesn't match directly - check Firebase to see if user is a test user
    if (verifyInFirebase) {
      try {
        firebaseLogger.debug('Email does not match directly, checking Firebase');
        const userData = await fetchUserByEmail(emailLower);
        
        if (!userData) {
          firebaseLogger.info('Access denied - user not found in database');
          return { hasAccess: false, error: 'User not found in database. Please ensure your email is registered and try again.' };
        }
        
        firebaseLogger.debug('User found, checking if user is a test user');
        const userIsTestUser = isTestUser(userData);
        firebaseLogger.debug('User is test user', {
          name: userData.name,
          primary_email: userData.primary_email,
          email: userData.email
        });
        
        // Check if user matches admin criteria using full user data (name, email, etc.)
        if (userIsTestUser) {
          firebaseLogger.info('Access granted - user is a test user');
          return { hasAccess: true, userData };
        }
        
        firebaseLogger.info('Access denied - user is not a test user');
        return { hasAccess: false, error: 'Access denied. Only test/development users can access the dashboard.' };
      } catch (firebaseError) {
        firebaseLogger.error('Error fetching user from Firebase', { error: firebaseError });
        return { hasAccess: false, error: `Error verifying user: ${firebaseError.message}. Please try again.` };
      }
    }
    
    // Email doesn't match and Firebase verification is disabled
    firebaseLogger.info('Access denied - email does not match and Firebase verification disabled');
    return { hasAccess: false, error: 'Access denied. Only authorized admin users can access the dashboard.' };
  } catch (error) {
    firebaseLogger.error('Error verifying admin access', { error });
    return { hasAccess: false, error: `An error occurred while verifying access: ${error.message}` };
  }
};

/**
 * Fetch user data from Firebase by email
 * @param {string} email - Email address to search for
 * @returns {Promise<Object|null>} - User data object or null if not found
 */
const fetchUserByEmail = async (email) => {
  try {
    const emailLower = email.toLowerCase().trim();
    firebaseLogger.debug('Searching for user with email', { email: emailLower });
    
    const usersRef = collection(db, 'users');
    
    // Try with 'primary_email' field first (case-insensitive by fetching all and filtering)
    const allUsersSnapshot = await getDocs(usersRef);
    firebaseLogger.debug('Found total users in database', { count: allUsersSnapshot.size });
    
    // Search through all users for case-insensitive match
    for (const doc of allUsersSnapshot.docs) {
      const userData = doc.data();
      const primaryEmail = (userData.primary_email || '').toLowerCase().trim();
      const userEmail = (userData.email || '').toLowerCase().trim();
      
      if (primaryEmail === emailLower || userEmail === emailLower) {
        firebaseLogger.debug('Found user', {
          id: doc.id,
          name: userData.name,
          primary_email: userData.primary_email,
          email: userData.email,
          isTestUser: isTestUser(userData)
        });
        return userData;
      }
    }
    
    firebaseLogger.debug('User not found with email', { email: emailLower });
    return null;
  } catch (error) {
    firebaseLogger.error('Error fetching user by email', { error });
    throw error;
  }
};

// Data validation and sanitization functions
export const validateUserData = (userData) => {
  return {
    id: userData.id || userData.firebase_uid || 'unknown',
    email: userData.primary_email || userData.email || 'no-email',
    name: userData.name || 'Anonymous',
    // Check both snake_case (primary) and camelCase (fallback) for email verification
    emailVerified: Boolean(userData.email_verified || userData.emailVerified),
    hasCompletedOnboarding: Boolean(userData.hasCompletedOnboarding),
    walletAddress: userData.wallet_address || userData.walletAddress || null,
    walletStatus: userData.wallet_status || userData.walletStatus || 'unknown',
    walletType: userData.wallet_type || userData.walletType || 'unknown',
    // Check both camelCase (primary) and snake_case (fallback) for last login
    lastLoginAt: userData.lastLoginAt || userData.last_login_at || null,
    lastVerifiedAt: userData.lastVerifiedAt || userData.last_verified_at || null,
    // Check both snake_case (primary) and camelCase (fallback) for created date
    createdAt: userData.created_at || userData.createdAt || null,
    // Check both snake_case (primary) and camelCase (fallback) for migration
    migrationCompleted: Boolean(userData.migration_completed || userData.migrationCompleted),
    migrationVersion: userData.migration_version || userData.migrationVersion || 'unknown',
    // Additional fields from actual structure
    status: userData.status || 'unknown',
    points: userData.points || 0,
    referralCode: userData.referral_code || userData.referralCode || null,
    referredBy: userData.referred_by || userData.referredBy || null,
    phone: userData.phone || null,
    phoneVerified: Boolean(userData.phoneVerified),
    primaryPhone: userData.primary_phone || userData.primaryPhone || null
  };
};

// Helper function to safely get numeric values
const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

// Helper function to safely get percentage
/**
 * Safely calculate percentage
 * @param {number} numerator - Numerator value
 * @param {number} denominator - Denominator value
 * @param {number} [decimals=1] - Number of decimal places
 * @returns {number} - Percentage as number (0 if invalid)
 */
const safePercentage = (numerator, denominator, decimals = 1) => {
  if (!denominator || denominator === 0 || isNaN(denominator)) return 0;
  if (isNaN(numerator)) return 0;
  const percent = (numerator / denominator) * 100;
  return isNaN(percent) ? 0 : parseFloat(percent.toFixed(decimals));
};

/**
 * Get user analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @param {string|null} [platform=null] - Platform filter ('ios', 'android', null)
 * @returns {Promise<Object>} - User analytics data
 */
export const getUserAnalytics = async (period = FILTER_PERIODS.DAY, platform = null) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    // Get all users (we'll filter in memory due to date string format)
    const usersRef = collection(db, 'users');
    const allUsersSnapshot = await getDocs(usersRef);
    
    let totalUserCount = 0;
    let periodUserCount = 0;
    let iosUsers = 0;
    let androidUsers = 0;
    let verifiedUsers = 0;
    let onboardedUsers = 0;
    let walletUsers = 0;
    let healthyWallets = 0;
    let activeUsers = 0; // Users with wallets who logged in during the period
    let migrationCompleted = 0;
    
    
    allUsersSnapshot.forEach(doc => {
      const rawUserData = doc.data();
      const userData = validateUserData(rawUserData);
      
      // Exclude test/development users from analytics
      if (isTestUser(rawUserData)) {
        return; // Skip this user
      }
      
      // Apply platform filter if specified
      if (platform) {
        // Simple platform detection based on wallet type and other indicators
        const userPlatform = userData.walletType === 'app-generated' ? 'mobile' : 'web';
        if (platform === 'ios' && userPlatform !== 'mobile') return;
        if (platform === 'android' && userPlatform !== 'mobile') return;
        if (platform === 'web' && userPlatform !== 'web') return;
      }
      
      totalUserCount++;
      
      // Check if user was created in the period
      if (userData.createdAt && isDateInPeriod(userData.createdAt, startDate, endDate)) {
        periodUserCount++;
      }
      
      // Count verified users
      if (userData.emailVerified) {
        verifiedUsers++;
      }
      
      // Count onboarded users
      if (userData.hasCompletedOnboarding) {
        onboardedUsers++;
      }
      
      // Count users with wallets
      if (userData.walletAddress) {
        walletUsers++;
        
        // Count healthy wallets
        if (userData.walletStatus === 'healthy') {
          healthyWallets++;
        }
        
        // Count active users (users with wallets who logged in during the period)
        if (userData.lastLoginAt && isDateInPeriod(userData.lastLoginAt, startDate, endDate)) {
          activeUsers++;
        }
        
        // Platform detection based on wallet type
        if (userData.walletType === 'app-generated') {
          // This indicates mobile app usage - could be iOS or Android
          // For now, we'll count as mobile users
          iosUsers++; // Simplified - would need better detection
        }
      }
      
      // Count migration completed users
      if (userData.migrationCompleted) {
        migrationCompleted++;
      }
      
      // 🆕 Count phone authentication users
      if (userData.phone || userData.primary_phone) {
        // Tracked separately in getPhoneAuthAnalytics
      }
      
      // 🆕 Count users with badges
      if (userData.badges && Array.isArray(userData.badges) && userData.badges.length > 0) {
        // Tracked separately in getBadgeAnalytics
      }
    });
    
    // Get waitlist data (filtered for test users)
    let waitlistCount = 0;
    try {
      const waitlistRef = collection(db, 'Waitlist');
      const waitlistSnapshot = await getDocs(waitlistRef);
      
      waitlistSnapshot.forEach(doc => {
        const waitlistData = doc.data();
        
        // Filter out test users from waitlist
        const email = waitlistData.email || waitlistData.user_email || waitlistData.primary_email;
        if (email && isTestUserEmail(email)) {
          return; // Skip test user waitlist entries
        }
        
        if (isDateInPeriod(waitlistData.created_at, startDate, endDate)) {
          waitlistCount++;
        }
      });
    } catch (waitlistError) {
      firebaseLogger.warn('Could not fetch waitlist data', { error: waitlistError.message });
    }
    
    const result = {
      totalUserCount,
      periodUserCount,
      totalIosUserCount: iosUsers,
      totalAndroidUserCount: androidUsers,
      verifiedUsers,
      onboardedUsers,
      walletUsers,
      healthyWallets,
      activeUsers, // Users with wallets who logged in during the period
      migrationCompleted,
      waitlistCount,
      period,
      platform,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    firebaseLogger.error('Error fetching user analytics', { error });
    throw error;
  }
};

// Session Analytics - Audited and Fixed
/**
 * Get session analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Session analytics data
 */
export const getSessionAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    // Get all users and filter in memory due to date string format
    const usersRef = collection(db, 'users');
    const allUsersSnapshot = await getDocs(usersRef);
    
    let totalSessions = 0;
    let activeUsers = 0;
    let recentlyVerifiedUsers = 0;
    let recentLogins = 0;
    let recentVerifications = 0;
    
    
    allUsersSnapshot.forEach(doc => {
      const rawUserData = doc.data();
      const userData = validateUserData(rawUserData);
      
      // Exclude test/development users from analytics
      if (isTestUser(rawUserData)) {
        return; // Skip this user
      }
      
      // Check if user logged in during the period
      if (userData.lastLoginAt && isDateInPeriod(userData.lastLoginAt, startDate, endDate)) {
        activeUsers++;
        totalSessions++;
        recentLogins++;
      }
      
      // Check if user was verified during the period
      if (userData.lastVerifiedAt && isDateInPeriod(userData.lastVerifiedAt, startDate, endDate)) {
        recentlyVerifiedUsers++;
        recentVerifications++;
      }
    });
    
    const averageSessionCountPerUser = activeUsers > 0 ? totalSessions / activeUsers : 0;
    
    const result = {
      totalSessions,
      averageSessionCountPerUser,
      activeUsers,
      recentlyVerifiedUsers,
      recentLogins,
      recentVerifications,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    firebaseLogger.error('Error fetching session analytics', { error });
    throw error;
  }
};

// Crash Analytics
/**
 * Get crash analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Crash analytics data
 */
export const getCrashAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { start, end } = getDateRange(period);
    
    const crashesRef = collection(db, 'crashes');
    const crashesQuery = query(
      crashesRef,
      where('timestamp', '>=', start),
      where('timestamp', '<=', end)
    );
    
    const crashesSnapshot = await getCountFromServer(crashesQuery);
    const totalCrashCount = crashesSnapshot.data().count;
    
    // Platform breakdown for crashes
    const iosCrashesQuery = query(
      crashesRef,
      where('timestamp', '>=', start),
      where('timestamp', '<=', end),
      where('platform', '==', 'ios')
    );
    const androidCrashesQuery = query(
      crashesRef,
      where('timestamp', '>=', start),
      where('timestamp', '<=', end),
      where('platform', '==', 'android')
    );
    
    const [iosCrashesSnapshot, androidCrashesSnapshot] = await Promise.all([
      getCountFromServer(iosCrashesQuery),
      getCountFromServer(androidCrashesQuery)
    ]);
    
    return {
      totalCrashCount,
      totalCrashCountByPlatform: {
        ios: iosCrashesSnapshot.data().count,
        android: androidCrashesSnapshot.data().count
      },
      period
    };
  } catch (error) {
    firebaseLogger.error('Error fetching crash analytics', { error });
    throw error;
  }
};

// Split Analytics
// Split Analytics - Comprehensive Multi-Collection Query
/**
 * Get split analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Split analytics data
 */
export const getSplitAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting comprehensive split analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Helper: Validate and normalize financial amounts
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      const coerceToNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number' && isFinite(value)) return value;
        const s = String(value).replace(/[^0-9.+-]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
      };
      
      let normalized = coerceToNumber(amount);
      
      // Check if amount might be in cents
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        const originalAmount = normalized;
        normalized = normalized / 100;
        console.log(`💰 ${context}: Converted cents to dollars: ${originalAmount} cents → $${normalized.toFixed(2)}`);
      }
      
      if (normalized < 0) {
        console.warn(`⚠️ ${context}: Negative amount: $${normalized}. Setting to 0.`);
        return 0;
      }
      if (normalized > maxAmount) {
        console.warn(`⚠️ ${context}: Suspiciously large amount: $${normalized.toFixed(2)}. Max: $${maxAmount}. Setting to 0.`);
        return 0;
      }
      
      return normalized;
    };
    
    // Only use 'splits' collection - splitWallets are tracked separately in shared wallets
    const splitsRef = collection(db, 'splits');
    const splitsSnapshot = await getDocs(splitsRef);
    
    console.log(`📊 Found ${splitsSnapshot.size} documents in splits collection`);
    
    // Get user IDs for test user filtering
    const splitUserIds = new Set();
    splitsSnapshot.forEach(doc => {
      const splitData = doc.data();
      const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
      if (userId) splitUserIds.add(userId);
    });
    const testUserMap = await batchCheckTestUsers(Array.from(splitUserIds));
    
    // Track processed splits to avoid duplicates
    const processedSplits = new Set();
    
    let totalSplits = 0;
    let totalAmount = 0;
    let activeSplits = 0;
    let completedSplits = 0;
    let pendingSplits = 0;
    let fairSplits = 0;
    let degenSplits = 0;
    let averageSplitAmount = 0;
    let mostActiveUsers = [];
    let currencyBreakdown = {};
    let splitTypeBreakdown = {};
    let merchantBreakdown = {};
    let totalParticipants = 0;
    let totalItems = 0;
    let totalAmountOwed = 0;
    let totalAmountPaid = 0;
    let splitWalletsCount = 0;
    let totalWalletAmount = 0;
    
    let skippedTestUsers = 0;
    let skippedDevnet = 0;
    let skippedOutOfPeriod = 0;
    let skippedInvalidAmounts = 0;
    let skippedDuplicates = 0;
    
    splitsSnapshot.forEach(doc => {
      const splitData = doc.data();
      const splitId = doc.id;
      
      // Deduplication - check if we've already processed this split
      if (processedSplits.has(splitId)) {
        skippedDuplicates++;
        return;
      }
      
      // Filter out test users
      const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
      if (userId && testUserMap.get(userId)) {
        skippedTestUsers++;
        return; // Skip test user splits
      }
      
      // Also check by name/email
      const creatorName = splitData.creatorName || splitData.name || '';
      if (creatorName && isTestUser({ name: creatorName })) {
        skippedTestUsers++;
        return; // Skip test user splits
      }
      
      // Filter out devnet transactions
      if (isDevnetTransaction(splitData)) {
        skippedDevnet++;
        return; // Skip devnet splits
      }
      
      // Normalize data structure
      const normalizedData = { ...splitData };
        normalizedData.createdAt = splitData.createdAt || splitData.created_at || splitData.timestamp;
        normalizedData.totalAmount = splitData.totalAmount || splitData.amount || 0;
      normalizedData.status = splitData.status || splitData.state || 'pending';
      normalizedData.splitType = splitData.splitType || splitData.type || '';
      normalizedData.description = splitData.description || splitData.title || splitData.name || '';
      
      // Check if split was created in the period
      if (!normalizedData.createdAt || !isDateInPeriod(normalizedData.createdAt, startDate, endDate)) {
        skippedOutOfPeriod++;
        return; // Skip splits outside the period
      }
        
      // Process amount with validation
      const amount = validateAndNormalizeAmount(
        normalizedData.totalAmount || 0,
        `split/${splitId}`,
        50000 // Lower max for splits (50K instead of 100K)
      );
      
      if (amount <= 0 || amount > 50000) {
        skippedInvalidAmounts++;
        return; // Skip invalid amounts
      }
      
      // Mark as processed
      processedSplits.add(splitId);
      
      totalSplits++;
        totalAmount += amount;
      
      // Debug logging for large splits
      if (amount > 1000) {
        console.log(`💵 Large split: ${splitId} - $${amount.toFixed(2)}`);
      }
        
        // Process status
      const status = normalizedData.status.toLowerCase();
      if (status === 'active') {
          activeSplits++;
      } else if (status === 'completed' || status === 'finished' || status === 'settled') {
          completedSplits++;
      } else if (status === 'pending' || status === 'waiting') {
          pendingSplits++;
        }
        
        // Categorize splits based on splitType field and description/merchant
        const splitType = normalizedData.splitType || '';
        const description = (normalizedData.description || '').toLowerCase();
      const merchantName = (normalizedData.merchant?.name || '').toLowerCase();
        
      // Check for degen split indicators
        const hasDegenLoser = normalizedData.degenLoser || 
                              normalizedData.degen_loser || 
                              normalizedData.degenLoserId ||
                              (normalizedData.participants && normalizedData.participants.some(p => p.isLoser || p.role === 'loser'));
        
        if (hasDegenLoser || splitType === 'degen' || description.includes('degen')) {
          degenSplits++;
      } else if (splitType === 'fair' || description.includes('fair nav') || description.includes('fair') || merchantName.includes('fair')) {
          fairSplits++;
        }
        
        // Track currency breakdown
        const currency = normalizedData.currency || 'Unknown';
        currencyBreakdown[currency] = (currencyBreakdown[currency] || 0) + 1;
        
        // Track split type breakdown
        const splitTypeValue = normalizedData.splitType || 'unknown';
        splitTypeBreakdown[splitTypeValue] = (splitTypeBreakdown[splitTypeValue] || 0) + 1;
        
        // Track merchant breakdown
        if (normalizedData.merchant && normalizedData.merchant.name) {
          const merchantName = normalizedData.merchant.name;
          merchantBreakdown[merchantName] = (merchantBreakdown[merchantName] || 0) + 1;
        }
        
        // Count participants and process participant data
        if (normalizedData.participants && Array.isArray(normalizedData.participants)) {
          totalParticipants += normalizedData.participants.length;
          
        // Process participant financial data with validation
        normalizedData.participants.forEach((participant, idx) => {
          const owed = validateAndNormalizeAmount(
            participant.amountOwed || 0,
            `split/${splitId}/participant/${idx}/owed`,
            50000
          );
          const paid = validateAndNormalizeAmount(
            participant.amountPaid || 0,
            `split/${splitId}/participant/${idx}/paid`,
            50000
          );
          
          if (owed > 0 && owed <= 50000) {
            totalAmountOwed += owed;
          }
          if (paid > 0 && paid <= 50000) {
            totalAmountPaid += paid;
            }
          });
        }
        
        // Count items
        if (normalizedData.items && Array.isArray(normalizedData.items)) {
          totalItems += normalizedData.items.length;
        }
        
        // Track most active users (exclude test users)
      if (userId && !testUserMap.get(userId)) {
            const existingUser = mostActiveUsers.find(u => u.userId === userId);
            if (existingUser) {
              existingUser.splitCount++;
              existingUser.totalAmount += amount;
            } else {
              mostActiveUsers.push({
                userId,
            name: creatorName || 'Unknown',
                splitCount: 1,
                totalAmount: amount,
                walletAddress: splitData.walletAddress || 'N/A'
              });
        }
      }
    });
    
    // Log skipped items for debugging
    console.log(`📊 Split Analytics Summary:`);
    console.log(`  ✅ Processed: ${totalSplits} splits, Total: $${totalAmount.toFixed(2)}`);
    console.log(`  ⏭️ Skipped - Test users: ${skippedTestUsers}, Devnet: ${skippedDevnet}, Out of period: ${skippedOutOfPeriod}, Invalid amounts: ${skippedInvalidAmounts}, Duplicates: ${skippedDuplicates}`);
    
    // Process splitWallets separately (these are tracked in shared wallets analytics, not here)
    // We don't count splitWallets in split analytics to avoid double counting
    
    averageSplitAmount = totalSplits > 0 ? totalAmount / totalSplits : 0;
    const averageParticipants = totalSplits > 0 ? totalParticipants / totalSplits : 0;
    const averageItems = totalSplits > 0 ? totalItems / totalSplits : 0;
    
    // Sort most active users by split count
    mostActiveUsers.sort((a, b) => b.splitCount - a.splitCount);
    
    const result = {
      totalSplits,
      totalAmount,
      activeSplits,
      completedSplits,
      pendingSplits,
      fairSplits,
      degenSplits,
      averageSplitAmount,
      averageParticipants,
      averageItems,
      totalParticipants,
      totalItems,
      totalAmountOwed,
      totalAmountPaid,
      splitWalletsCount: 0, // Tracked separately in shared wallets
      totalWalletAmount: 0, // Tracked separately in shared wallets
      mostActiveUsers: mostActiveUsers.slice(0, 5),
      currencyBreakdown,
      splitTypeBreakdown,
      merchantBreakdown,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching split analytics:', error);
    throw error;
  }
};

// Financial Flow Analytics - Separated by Category
/**
 * Get separated financial flow analytics - splits, requests, shared wallets, user wallets
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Separated financial flow data
 */
export const getFinancialFlowAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting separated financial flow analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Helper: Validate and normalize financial amounts
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      const coerceToNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number' && isFinite(value)) return value;
        const s = String(value).replace(/[^0-9.+-]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
      };
      
      let normalized = coerceToNumber(amount);
      
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        const originalAmount = normalized;
        normalized = normalized / 100;
        console.log(`💰 ${context}: Converted cents to dollars: ${originalAmount} cents → $${normalized.toFixed(2)}`);
      }
      
      if (normalized < 0) {
        console.warn(`⚠️ ${context}: Negative amount: $${normalized}. Setting to 0.`);
        return 0;
      }
      if (normalized > maxAmount) {
        console.warn(`⚠️ ${context}: Suspiciously large amount: $${normalized.toFixed(2)}. Max: $${maxAmount}. Setting to 0.`);
        return 0;
      }
      
      return normalized;
    };
    
    // Initialize separated categories
    const splits = {
      count: 0,
      totalValue: 0,
      averageValue: 0,
      completed: {
        count: 0,
        totalValue: 0,
        averageValue: 0
      },
      pending: {
        count: 0,
        totalValue: 0,
        averageValue: 0
      },
      active: {
        count: 0,
        totalValue: 0,
        averageValue: 0
      },
      transactions: []
    };
    
    const requests = {
      count: 0,
      totalValue: 0,
      averageValue: 0,
      paymentRequests: 0,
      withdrawalRequests: 0,
      transactions: []
    };
    
    const sharedWallets = {
      count: 0,
      totalBalance: 0,
      totalFunding: 0,
      totalWithdrawals: 0,
      netFlow: 0,
      transactions: []
    };
    
    const userWallets = {
      count: 0,
      totalBalance: 0,
      totalDeposits: 0,
      totalWithdrawals: 0,
      netFlow: 0,
      transactions: []
    };
    
    // Get test user IDs for filtering
    const splitsRef = collection(db, 'splits');
    const splitsSnapshot = await getDocs(splitsRef);
    const splitUserIds = new Set();
    splitsSnapshot.forEach(doc => {
      const splitData = doc.data();
      const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
      if (userId) splitUserIds.add(userId);
    });
    const testUserMap = await batchCheckTestUsers(Array.from(splitUserIds));
    
    // Track processed splits to avoid duplicates
    const processedSplits = new Set();
    
    // 1. PROCESS SPLITS (1:1 transfers)
    try {
      splitsSnapshot.forEach(doc => {
        const splitId = doc.id;
        
        // Deduplication
        if (processedSplits.has(splitId)) {
          return; // Skip duplicate
        }
        const splitData = doc.data();
        
        // Filter out test users
        const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
        if (userId && testUserMap.get(userId)) {
          return; // Skip test user splits
        }
        
        const createdAt = splitData.createdAt || splitData.created_at || splitData.timestamp;
        
        if (createdAt && isDateInPeriod(createdAt, startDate, endDate)) {
          const amount = validateAndNormalizeAmount(
            splitData.totalAmount || splitData.amount || 0,
            `split/${doc.id}`,
            50000 // Lower max for splits
          );
          
          if (amount > 0 && amount <= 50000) {
            // Mark as processed
            processedSplits.add(splitId);
            
            const status = (splitData.status || 'pending').toLowerCase();
            
            splits.count++;
            splits.totalValue += amount;
            
            // Separate by status
            if (status === 'completed' || status === 'finished' || status === 'settled') {
              splits.completed.count++;
              splits.completed.totalValue += amount;
            } else if (status === 'pending' || status === 'waiting') {
              splits.pending.count++;
              splits.pending.totalValue += amount;
            } else {
              // Active or other statuses
              splits.active.count++;
              splits.active.totalValue += amount;
            }
            
            splits.transactions.push({
              id: doc.id,
              amount,
              type: 'split',
              status,
              createdAt
            });
          }
        }
      });
      
      // Calculate averages
      splits.averageValue = splits.count > 0 ? splits.totalValue / splits.count : 0;
      splits.completed.averageValue = splits.completed.count > 0 ? splits.completed.totalValue / splits.completed.count : 0;
      splits.pending.averageValue = splits.pending.count > 0 ? splits.pending.totalValue / splits.pending.count : 0;
      splits.active.averageValue = splits.active.count > 0 ? splits.active.totalValue / splits.active.count : 0;
    } catch (error) {
      console.log(`⚠️ Could not query splits:`, error.message);
    }
    
    // 2. PROCESS PAYMENT/WITHDRAWAL REQUESTS
    try {
      const paymentRequestsRef = collection(db, 'paymentRequests');
      const paymentRequestsSnapshot = await getDocs(paymentRequestsRef);
      
      // Get user IDs for test user filtering
      const requestUserIds = new Set();
      paymentRequestsSnapshot.forEach(doc => {
        const requestData = doc.data();
        const userId = requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
        if (userId) requestUserIds.add(userId);
      });
      const requestTestUserMap = await batchCheckTestUsers(Array.from(requestUserIds));
      
      paymentRequestsSnapshot.forEach(doc => {
        const requestData = doc.data();
        
        // Filter out test users
        const userId = requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
        if (userId && requestTestUserMap.get(userId)) {
          return; // Skip test user requests
        }
        const email = requestData.email || requestData.userEmail || requestData.user_email;
        if (email && isTestUserEmail(email)) {
          return; // Skip test user requests
        }
        
        // Filter out devnet transactions
        if (isDevnetTransaction(requestData)) {
          return; // Skip devnet requests
        }
        
        const createdAt = requestData.createdAt || requestData.created_at || requestData.timestamp;
        
        if (createdAt && isDateInPeriod(createdAt, startDate, endDate)) {
          const amount = validateAndNormalizeAmount(
            requestData.amount || requestData.value || requestData.requestAmount || 0,
            `paymentRequest/${doc.id}`
          );
          
          if (amount > 0) {
            requests.count++;
            requests.paymentRequests++;
            requests.totalValue += amount;
            requests.transactions.push({
              id: doc.id,
              amount,
              type: 'payment_request',
              createdAt
            });
          }
        }
      });
    } catch (error) {
      console.log(`⚠️ Could not query paymentRequests:`, error.message);
    }
    
    try {
      const withdrawalRequestsRef = collection(db, 'withdrawalRequests');
      const withdrawalRequestsSnapshot = await getDocs(withdrawalRequestsRef);
      
      // Get user IDs for test user filtering
      const withdrawalUserIds = new Set();
      withdrawalRequestsSnapshot.forEach(doc => {
        const requestData = doc.data();
        const userId = requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
        if (userId) withdrawalUserIds.add(userId);
      });
      const withdrawalTestUserMap = await batchCheckTestUsers(Array.from(withdrawalUserIds));
      
      withdrawalRequestsSnapshot.forEach(doc => {
        const requestData = doc.data();
        
        // Filter out test users
        const userId = requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
        if (userId && withdrawalTestUserMap.get(userId)) {
          return; // Skip test user requests
        }
        const email = requestData.email || requestData.userEmail || requestData.user_email;
        if (email && isTestUserEmail(email)) {
          return; // Skip test user requests
        }
        
        // Filter out devnet transactions
        if (isDevnetTransaction(requestData)) {
          return; // Skip devnet requests
        }
        
        const createdAt = requestData.createdAt || requestData.created_at || requestData.timestamp;
        
        if (createdAt && isDateInPeriod(createdAt, startDate, endDate)) {
          const amount = validateAndNormalizeAmount(
            requestData.amount || requestData.value || requestData.withdrawalAmount || 0,
            `withdrawalRequest/${doc.id}`
          );
          
          if (amount > 0) {
            requests.count++;
            requests.withdrawalRequests++;
            requests.totalValue += amount;
            requests.transactions.push({
              id: doc.id,
              amount,
              type: 'withdrawal_request',
              createdAt
            });
          }
        }
      });
    } catch (error) {
      console.log(`⚠️ Could not query withdrawalRequests:`, error.message);
    }
    
    // 3. PROCESS SHARED WALLETS
    // Get user IDs for test user filtering (shared across wallets and transactions)
    let sharedWalletTestUserMap = new Map();
    
    try {
      const sharedWalletsRef = collection(db, 'sharedWallets');
      const sharedWalletsSnapshot = await getDocs(sharedWalletsRef);
      
      // Get user IDs for test user filtering
      const sharedWalletUserIds = new Set();
      sharedWalletsSnapshot.forEach(doc => {
        const walletData = doc.data();
        if (walletData.creatorId) sharedWalletUserIds.add(walletData.creatorId);
        if (walletData.members && Array.isArray(walletData.members)) {
          walletData.members.forEach(member => {
            if (member.userId) sharedWalletUserIds.add(member.userId);
          });
        }
      });
      sharedWalletTestUserMap = await batchCheckTestUsers(Array.from(sharedWalletUserIds));
      
      sharedWalletsSnapshot.forEach(doc => {
        const walletData = doc.data();
        
        // Filter out test user wallets
        if (walletData.creatorId && sharedWalletTestUserMap.get(walletData.creatorId)) {
          return; // Skip test user wallets
        }
        
        // Extract balance from shared wallet document (try multiple field names)
        const balance = validateAndNormalizeAmount(
          walletData.totalBalance || 
          walletData.balance || 
          walletData.currentBalance ||
          walletData.walletBalance ||
          0,
          `sharedWallet/${doc.id}/balance`,
          1000000 // Wallets can have larger balances
        );
        
        sharedWallets.count++;
        sharedWallets.totalBalance += balance;
      });
      
      firebaseLogger.debug('Shared Wallet Balance Summary', {
        walletsProcessed: sharedWallets.count,
        totalBalance: sharedWallets.totalBalance.toFixed(2)
      });
    } catch (error) {
      firebaseLogger.error('Could not query sharedWallets', { error: error.message });
    }
    
    try {
      const sharedWalletTransactionsRef = collection(db, 'sharedWalletTransactions');
      const sharedWalletTransactionsSnapshot = await getDocs(sharedWalletTransactionsRef);
      
      // Track processed transactions to prevent double counting
      const processedSharedTx = new Set();
      let skippedTestUsers = 0;
      let skippedDevnet = 0;
      let skippedInvalidAmounts = 0;
      let skippedDuplicates = 0;
      
      // Add transaction user IDs to test user map if not already included
      const sharedWalletTxUserIds = new Set();
      sharedWalletTransactionsSnapshot.forEach(doc => {
        const txData = doc.data();
        const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id;
        if (userId && !sharedWalletTestUserMap.has(userId)) {
          sharedWalletTxUserIds.add(userId);
        }
      });
      
      if (sharedWalletTxUserIds.size > 0) {
        const txTestUserMap = await batchCheckTestUsers(Array.from(sharedWalletTxUserIds));
        // Merge into main map
        txTestUserMap.forEach((isTest, userId) => {
          sharedWalletTestUserMap.set(userId, isTest);
        });
      }
      
      sharedWalletTransactionsSnapshot.forEach(doc => {
        const txData = doc.data();
        const txId = doc.id;
        
        // Deduplication
        if (processedSharedTx.has(txId)) {
          skippedDuplicates++;
          return;
        }
        
        // Filter out devnet transactions
        if (isDevnetTransaction(txData)) {
          skippedDevnet++;
          return; // Skip devnet transactions
        }
        
        // Filter out test users
        const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id;
        if (userId && sharedWalletTestUserMap.get(userId)) {
          skippedTestUsers++;
          return; // Skip test user transactions
        }
        
        const createdAt = txData.createdAt || txData.created_at || txData.timestamp;
        
        if (createdAt && isDateInPeriod(createdAt, startDate, endDate)) {
          const amount = validateAndNormalizeAmount(
            txData.amount || txData.value || 0,
            `sharedWalletTx/${txId}`,
            50000 // Lower max for wallet transactions
          );
          
          if (amount > 0 && amount <= 50000) {
            // Mark as processed
            processedSharedTx.add(txId);
            
            const txType = (txData.type || '').toLowerCase();
            
            if (txType === 'funding' || txType === 'deposit' || txType === 'input') {
              sharedWallets.totalFunding += amount;
              sharedWallets.transactions.push({
                id: doc.id,
                amount,
                type: 'funding',
                createdAt
              });
            } else if (txType === 'withdrawal' || txType === 'withdraw' || txType === 'output') {
              sharedWallets.totalWithdrawals += amount;
              sharedWallets.transactions.push({
                id: doc.id,
                amount,
                type: 'withdrawal',
                createdAt
              });
            }
          } else {
            skippedInvalidAmounts++;
          }
        }
      });
      
      sharedWallets.netFlow = sharedWallets.totalFunding - sharedWallets.totalWithdrawals;
      
      firebaseLogger.debug('Shared Wallet Transactions Summary', {
        transactionsProcessed: sharedWallets.transactions.length,
        totalFunding: sharedWallets.totalFunding.toFixed(2),
        totalWithdrawals: sharedWallets.totalWithdrawals.toFixed(2),
        netFlow: sharedWallets.netFlow.toFixed(2),
        skipped: {
          testUsers: skippedTestUsers,
          devnet: skippedDevnet,
          invalidAmounts: skippedInvalidAmounts,
          duplicates: skippedDuplicates
        }
      });
    } catch (error) {
      firebaseLogger.error('Could not query sharedWalletTransactions', { error: error.message });
    }
    
    // 4. PROCESS USER WALLETS (in-app wallets)
    try {
      // First, fetch actual user balances from users collection
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // Get user IDs for test user filtering
      const userIds = new Set();
      usersSnapshot.forEach(doc => {
        userIds.add(doc.id);
      });
      const testUserMap = await batchCheckTestUsers(Array.from(userIds));
      
      // Track users with wallets and their balances
      const usersWithWallets = new Set();
      let skippedTestUsers = 0;
      let skippedNoBalance = 0;
      
      usersSnapshot.forEach(doc => {
        const userData = doc.data();
        const userId = doc.id;
        
        // Filter out test users
        if (testUserMap.get(userId)) {
          skippedTestUsers++;
          return; // Skip test users
        }
        
        // Check if user has a wallet
        const walletAddress = userData.wallet_address || userData.walletAddress;
        if (!walletAddress) {
          return; // Skip users without wallets
        }
        
        // Extract balance from user document (try multiple field names)
        const balance = validateAndNormalizeAmount(
          userData.wallet_balance || 
          userData.walletBalance || 
          userData.balance || 
          userData.totalBalance ||
          userData.currentBalance ||
          0,
          `user/${userId}/balance`,
          1000000 // Wallets can have larger balances
        );
        
        if (balance > 0) {
          usersWithWallets.add(userId);
          userWallets.totalBalance += balance;
        } else {
          skippedNoBalance++;
        }
      });
      
      // Now fetch transactions for flow analysis (deposits/withdrawals in period)
      const transactionsRef = collection(db, 'transactions');
      const transactionsSnapshot = await getDocs(transactionsRef);
      
      const userTransactionsRef = collection(db, 'user_transactions');
      const userTransactionsSnapshot = await getDocs(userTransactionsRef);
      
      const allUserTxDocs = [];
      transactionsSnapshot.forEach(doc => {
        allUserTxDocs.push({ id: doc.id, collection: 'transactions', data: doc.data() });
      });
      userTransactionsSnapshot.forEach(doc => {
        allUserTxDocs.push({ id: doc.id, collection: 'user_transactions', data: doc.data() });
      });
      
      // Track processed transactions to prevent double counting
      const processedUserTx = new Set();
      let skippedDevnet = 0;
      let skippedInvalidAmounts = 0;
      let skippedDuplicates = 0;
      
      allUserTxDocs.forEach(({ id, collection, data: txData }) => {
        // Filter out test users
        const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id;
        if (userId && testUserMap.get(userId)) {
          return; // Skip test user transactions
        }
        
        // Filter out devnet transactions
        if (isDevnetTransaction(txData)) {
          skippedDevnet++;
          return; // Skip devnet transactions
        }
        
        // Deduplication
        const txKey = `${collection}:${id}`;
        if (processedUserTx.has(txKey)) {
          skippedDuplicates++;
          return;
        }
        
        const createdAt = txData.createdAt || txData.created_at || txData.timestamp;
        
        // Only count transactions within the period for flow analysis
        if (createdAt && isDateInPeriod(createdAt, startDate, endDate, `userWalletTx/${collection}/${id}`)) {
          const amount = validateAndNormalizeAmount(
            txData.amount || txData.value || 0,
            `userWalletTx/${collection}/${id}`,
            50000 // Lower max for user wallet transactions
          );
          
          if (amount > 0 && amount <= 50000) {
            // Mark as processed
            processedUserTx.add(txKey);
            
            const txType = (txData.type || txData.transactionType || '').toLowerCase();
            
            if (txType.includes('deposit') || txType.includes('input') || txType.includes('funding')) {
              userWallets.totalDeposits += amount;
              userWallets.transactions.push({
                id,
                amount,
                type: 'deposit',
                createdAt,
                collection
              });
            } else if (txType.includes('withdrawal') || txType.includes('withdraw') || txType.includes('output')) {
              userWallets.totalWithdrawals += amount;
              userWallets.transactions.push({
                id,
                amount,
                type: 'withdrawal',
                createdAt,
                collection
              });
            } else {
              // Other user wallet transactions (sends, receives, etc.) - these are internal transfers
              // Don't count as volume, just track for reference
              userWallets.transactions.push({
                id,
                amount,
                type: txType || 'other',
                createdAt,
                collection
              });
            }
          } else {
            skippedInvalidAmounts++;
          }
        }
      });
      
      // Set count (users with wallets) and net flow (from transactions in period)
      userWallets.count = usersWithWallets.size;
      // totalBalance is now the sum of actual user balances (not calculated from transactions)
      userWallets.netFlow = userWallets.totalDeposits - userWallets.totalWithdrawals; // Net flow in period
      
      firebaseLogger.debug('User Wallet Analytics Summary', {
        usersWithWallets: userWallets.count,
        totalBalance: userWallets.totalBalance.toFixed(2),
        transactions: userWallets.transactions.length,
        deposits: userWallets.totalDeposits.toFixed(2),
        withdrawals: userWallets.totalWithdrawals.toFixed(2),
        netFlow: userWallets.netFlow.toFixed(2),
        skipped: {
          testUsers: skippedTestUsers,
          noBalance: skippedNoBalance,
          devnet: skippedDevnet,
          invalidAmounts: skippedInvalidAmounts,
          duplicates: skippedDuplicates
        }
      });
    } catch (error) {
      firebaseLogger.error('Could not query user wallet data', { error: error.message });
    }
    
    // Calculate averages
    splits.averageValue = splits.count > 0 ? splits.totalValue / splits.count : 0;
    splits.completed.averageValue = splits.completed.count > 0 ? splits.completed.totalValue / splits.completed.count : 0;
    splits.pending.averageValue = splits.pending.count > 0 ? splits.pending.totalValue / splits.pending.count : 0;
    splits.active.averageValue = splits.active.count > 0 ? splits.active.totalValue / splits.active.count : 0;
    requests.averageValue = requests.count > 0 ? requests.totalValue / requests.count : 0;
    
    const result = {
      splits,
      requests,
      sharedWallets,
      userWallets,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    firebaseLogger.debug('Financial flow analytics complete', {
      splits: {
        count: splits.count,
        totalValue: splits.totalValue.toFixed(2)
      },
      requests: {
        count: requests.count,
        totalValue: requests.totalValue.toFixed(2)
      },
      sharedWallets: {
        count: sharedWallets.count,
        totalBalance: sharedWallets.totalBalance.toFixed(2),
        netFlow: sharedWallets.netFlow.toFixed(2)
      },
      userWallets: {
        totalBalance: userWallets.totalBalance.toFixed(2),
        totalDeposits: userWallets.totalDeposits.toFixed(2),
        totalWithdrawals: userWallets.totalWithdrawals.toFixed(2),
        netFlow: userWallets.netFlow.toFixed(2)
      }
    });
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching financial flow analytics:', error);
    throw error;
  }
};

// Transaction Analytics - Comprehensive Multi-Collection Query
/**
 * Get transaction analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Transaction analytics data
 */
export const getTransactionAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting comprehensive transaction analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Query multiple collections that might contain transaction data
    // Restrict to collections that exist in your project to avoid noisy 0-count logs
    const transactionCollections = [
      'transactions',
      'user_transactions',
      'moonpay_transactions',
      'paymentRequests',
      'withdrawalRequests'
    ];
    
    const allTransactionDocs = [];

    // Helper: robust number coercion for strings like "$1,234.56" or "1234"
    const coerceToNumber = (value) => {
      if (value == null) return 0;
      if (typeof value === 'number' && isFinite(value)) return value;
      const s = String(value).replace(/[^0-9.+-]/g, '');
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    };
    
    // Helper: Validate and normalize financial amounts (handles cents-to-dollars conversion)
    // This should be used for ALL financial values across the app
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      let normalized = coerceToNumber(amount);
      
      // Check if amount might be in cents (if it's a very large integer between 1000 and 1M)
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        // Likely in cents, convert to dollars
        const originalAmount = normalized;
        normalized = normalized / 100;
        console.log(`💰 ${context}: Converted cents to dollars: ${originalAmount} cents → $${normalized.toFixed(2)}`);
      }
      
      // Validate amount is reasonable
      if (normalized < 0) {
        console.warn(`⚠️ ${context}: Negative amount found: $${normalized}. Setting to 0.`);
        return 0;
      }
      if (normalized > maxAmount) {
        console.warn(`⚠️ ${context}: Suspiciously large amount: $${normalized.toFixed(2)}. Max allowed: $${maxAmount}. Setting to 0.`);
        return 0;
      }
      
      return normalized;
    };
    
    for (const collectionName of transactionCollections) {
      try {
        const collectionRef = collection(db, collectionName);
        const snapshot = await getDocs(collectionRef);
        if (snapshot.size > 0) {
          console.log(`📊 Found ${snapshot.size} documents in ${collectionName}`);
        }
        
        snapshot.forEach(doc => {
          allTransactionDocs.push({
            id: doc.id,
            collection: collectionName,
            data: doc.data()
          });
        });
      } catch (error) {
        console.log(`⚠️ Could not query ${collectionName}:`, error.message);
      }
    }
    
    console.log(`📊 Total transaction-related documents found: ${allTransactionDocs.length}`);
    
    // Extract all unique user IDs from transactions for batch checking
    const userIds = new Set();
    allTransactionDocs.forEach(({ data: txData }) => {
      const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id || 
                     txData.requesterId || txData.requester_id || txData.fromUserId || txData.from_user_id;
      if (userId) userIds.add(userId);
    });
    
    // Batch check all user IDs for test users
    const testUserMap = await batchCheckTestUsers(Array.from(userIds));
    console.log(`[Test User Filter] Checked ${userIds.size} unique users, found ${Array.from(testUserMap.values()).filter(Boolean).length} test users`);
    
    let totalTransactionCount = 0;
    let totalTransactionValue = 0;
    let totalRequestCount = 0;
    let totalSendCount = 0;
    let totalRequestVolume = 0;
    let totalSendVolume = 0;
    let totalInputVolume = 0;
    let totalWithdrawalVolume = 0;
    let moonpayVolume = 0;
    let paymentRequestsCount = 0;
    let withdrawalRequestsCount = 0;
    
    // Additional analytics from audit findings
    let totalGasFees = 0;
    let totalCompanyFees = 0;
    let blockchainNetworks = {};
    let deviceInfo = {};
    let appVersions = {};
    let transactionMethods = {};
    let gasFeeCoveredByCompany = 0;
    
    // Track processed transactions to prevent double counting
    // Use a combination of transaction ID and collection as unique key
    const processedTransactions = new Set();
    
    // Debug counters
    let skippedTestUsers = 0;
    let skippedDuplicates = 0;
    let skippedInvalidAmounts = 0;
    let skippedOutOfPeriod = 0;
    let skippedDevnet = 0;
    let skippedNoFees = 0;
    let skippedNotCompleted = 0;
    let skippedRequests = 0;
    let processedCount = 0;
    
    // Helper to get a unique transaction identifier
    const getTransactionKey = (docId, collection, txData) => {
      // Try to find a unique transaction identifier
      const txHash = txData.transactionHash || txData.txHash || txData.hash;
      const txIdField = txData.transactionId || txData.txId || txData.id;
      
      // If we have a transaction hash, use that (most reliable)
      if (txHash) {
        return `hash:${txHash}`;
      }
      
      // If we have a transaction ID field, use that
      if (txIdField && txIdField !== docId) {
        return `txid:${txIdField}`;
      }
      
      // For payment/withdrawal requests, use the request ID
      if (collection === 'paymentRequests' || collection === 'withdrawalRequests') {
        return `request:${collection}:${docId}`;
      }
      
      // Fallback: use collection + document ID
      return `${collection}:${docId}`;
    };
    
    // Helper to safely extract amount with validation
    const extractAmount = (txData, collection, docId) => {
      let rawAmount = 0;
      
      // Collection-specific amount extraction (more precise)
      if (collection === 'paymentRequests') {
        rawAmount = txData.amount || txData.requestAmount || txData.value || 0;
      } else if (collection === 'withdrawalRequests') {
        rawAmount = txData.amount || txData.withdrawalAmount || txData.value || 0;
      } else if (collection === 'moonpay_transactions') {
        rawAmount = txData.amount || txData.value || txData.totalAmount || 0;
      } else {
        // For regular transactions, prefer amount over value
        // Avoid totalAmount as it might include fees
        rawAmount = txData.amount || txData.value || 0;
      }
      
      // Use the centralized validation function
      return validateAndNormalizeAmount(rawAmount, `${collection}/${docId}`);
    };
    
    // Process all transaction documents
    allTransactionDocs.forEach(({ id, collection, data: txData }) => {
      // Check if transaction belongs to a test user
      // For transactions collection, check from_user and to_user (snake_case primary from mobile app)
      // Also check camelCase variants and other common field names
      const fromUserId = txData.from_user || txData.fromUser || txData.fromUserId || txData.from_user_id;
      const toUserId = txData.to_user || txData.toUser || txData.toUserId || txData.to_user_id;
      // Also check generic userId fields
      const userId = txData.userId || txData.user_id || txData.creatorId || txData.creator_id || 
                    txData.requesterId || txData.requester_id;
      // Skip if any user involved is a test user
      if ((fromUserId && testUserMap.get(fromUserId)) || 
          (toUserId && testUserMap.get(toUserId)) || 
          (userId && testUserMap.get(userId))) {
        skippedTestUsers++;
        return; // Skip this transaction - belongs to test user
      }
      
      // Also check email field if present (for payment requests, etc.)
      const email = txData.email || txData.userEmail || txData.user_email;
      if (email && isTestUserEmail(email)) {
        skippedTestUsers++;
        return; // Skip this transaction - belongs to test user
      }
      
      // Get unique transaction key for deduplication
      const txKey = getTransactionKey(id, collection, txData);
      
      // Skip if we've already processed this transaction
      if (processedTransactions.has(txKey)) {
        skippedDuplicates++;
        return;
      }
      
      // Normalize data structure based on collection type
      let normalizedData = { ...txData };
      // Derive commonly used fields from alternate shapes
      // CreatedAt aliasing (supports Timestamp, millis, and strings)
      normalizedData.createdAt = normalizedData.createdAt || normalizedData.created_at || normalizedData.timestamp || normalizedData.time || normalizedData.date || normalizedData.created || normalizedData.createdISO;
      if (!normalizedData.createdAt && (typeof txData?.createdAt === 'object') && (txData.createdAt?.seconds || txData.createdAt?._seconds)) {
        const sec = txData.createdAt.seconds ?? txData.createdAt._seconds;
        normalizedData.createdAt = new Date(sec * 1000).toISOString();
      }
      if (!normalizedData.createdAt && typeof txData?.timestamp === 'object' && (txData.timestamp?.seconds || txData.timestamp?._seconds)) {
        const sec = txData.timestamp.seconds ?? txData.timestamp._seconds;
        normalizedData.createdAt = new Date(sec * 1000).toISOString();
      }
      if (typeof normalizedData.createdAt === 'number' && normalizedData.createdAt > 1000000000) {
        // treat as epoch millis/seconds
        const ms = normalizedData.createdAt < 2000000000 ? normalizedData.createdAt * 1000 : normalizedData.createdAt;
        normalizedData.createdAt = new Date(ms).toISOString();
      }
      
      // Extract amount using improved extraction logic
      normalizedData.amount = extractAmount(txData, collection, id);
      // Gas fee aliases
      normalizedData.gas_fee = coerceToNumber(
        normalizedData.gas_fee ?? normalizedData.gasFee ?? normalizedData.fee?.gas ?? normalizedData.fees?.gas ??
        normalizedData.meta?.fees?.gas ?? normalizedData.meta?.gas?.fee ?? normalizedData.tx?.gasFee ?? normalizedData.tx?.fees?.gas
      );
      // Company fee aliases
      normalizedData.company_fee = coerceToNumber(
        normalizedData.company_fee ?? normalizedData.companyFee ?? normalizedData.fee?.company ?? normalizedData.fees?.company ??
        normalizedData.meta?.fees?.company ?? normalizedData.tx?.fees?.company
      );
      // Network aliases (blockchain_network is primary snake_case from mobile app)
      normalizedData.blockchain_network = (
        normalizedData.blockchain_network || normalizedData.network || normalizedData.networkName || normalizedData.chain || normalizedData.chain_name || normalizedData.chainId || 'unknown'
      );
      // Transaction signature/hash (tx_hash is primary snake_case from mobile app)
      normalizedData.tx_hash = normalizedData.tx_hash || normalizedData.txHash || normalizedData.transactionSignature || normalizedData.transaction_signature || normalizedData.signature || null;
      // Device/app/method aliases (snake_case primary from mobile app)
      normalizedData.device_info = normalizedData.device_info || normalizedData.deviceInfo || normalizedData.device || normalizedData.meta?.device || normalizedData.context?.device;
      normalizedData.app_version = normalizedData.app_version || normalizedData.appVersion || normalizedData.meta?.appVersion || normalizedData.context?.appVersion;
      normalizedData.transaction_method = normalizedData.transaction_method || normalizedData.transactionMethod || normalizedData.method || normalizedData.paymentMethod || normalizedData.meta?.method;
      // Transaction user fields (from_user/to_user are primary snake_case from mobile app)
      normalizedData.from_user = normalizedData.from_user || normalizedData.fromUser || normalizedData.fromUserId || null;
      normalizedData.to_user = normalizedData.to_user || normalizedData.toUser || normalizedData.toUserId || null;
      
      // Handle different collection structures (type and status only, amount already extracted)
      if (collection === 'moonpay_transactions') {
        normalizedData.type = txData.type || txData.transactionType || 'moonpay';
        normalizedData.status = txData.status || txData.state || 'completed';
      } else if (collection === 'paymentRequests') {
        normalizedData.type = txData.type || txData.requestType || 'payment_request';
        normalizedData.status = txData.status || txData.state || 'pending';
      } else if (collection === 'withdrawalRequests') {
        normalizedData.type = txData.type || txData.requestType || 'withdrawal';
        normalizedData.status = txData.status || txData.state || 'pending';
      } else if (collection === 'gas_fees') {
        normalizedData.gas_fee = coerceToNumber(txData.gas_fee || txData.amount || txData.fee || 0);
        normalizedData.blockchain_network = txData.blockchain_network || txData.network || 'unknown';
        // Gas fees don't count as transactions, skip to next
        return;
      } else if (collection === 'company_fees') {
        normalizedData.company_fee = coerceToNumber(txData.company_fee || txData.amount || txData.fee || 0);
        // Company fees don't count as transactions, skip to next
        return;
      }
      
      // Check if transaction was created in the period
      if (!normalizedData.createdAt || !isDateInPeriod(normalizedData.createdAt, startDate, endDate)) {
        skippedOutOfPeriod++;
        return;
      }
      
      // Mark this transaction as processed to prevent double counting
      processedTransactions.add(txKey);
      
      const amount = normalizedData.amount || 0;
      
      // Only count if amount is valid and reasonable
      if (amount > 0 && amount <= 50000) {
        processedCount++;
        
        // Process transaction type (use collection as fallback for type detection)
        const txType = (normalizedData.type || normalizedData.transactionType || '').toLowerCase();
        const txStatus = (normalizedData.status || normalizedData.state || '').toLowerCase();
        
        // Check if transaction has company fees (platform earned revenue)
        const hasCompanyFee = normalizedData.company_fee > 0;
        
        // Check if transaction is completed/successful (not pending, not request)
        const isCompleted = txStatus === 'completed' || 
                           txStatus === 'success' || 
                           txStatus === 'successful' || 
                           txStatus === 'settled' || 
                           txStatus === 'finished' ||
                           txStatus === 'executed' ||
                           (txStatus === '' && collection !== 'paymentRequests' && collection !== 'withdrawalRequests');
        
        // IMPORTANT: Only count transactions in volume that:
        // 1. Have company fees (platform earned revenue)
        // 2. Are completed/successful (actually occurred on platform)
        // 3. Are not requests (requests are not actual transactions)
        // 4. Are not internal transfers (already counted when deposited)
        
        // Collection-based type detection (more reliable)
        if (collection === 'paymentRequests' || txType.includes('request') || txType.includes('payment_request')) {
          // Payment requests are requests for money - track separately but DON'T count in global transaction value
          // Requests are not actual transactions until they're fulfilled
          skippedRequests++;
          totalRequestCount++;
          totalRequestVolume += amount;
          // DO NOT add to totalTransactionCount or totalTransactionValue - requests are not actual transactions
          // Count payment requests
          if (collection === 'paymentRequests') {
            paymentRequestsCount++;
          }
          firebaseLogger.debug('Payment request excluded from volume', { collection, id, amount: amount.toFixed(2) });
        } else if (collection === 'withdrawalRequests' || (txType.includes('withdrawal') && txType.includes('request'))) {
          // Withdrawal requests are requests - track separately but DON'T count in global transaction value
          // Withdrawals are actual money movement only when executed, not when requested
          skippedRequests++;
          totalWithdrawalVolume += amount;
          // DO NOT add to totalTransactionCount or totalTransactionValue - withdrawal requests are not actual transactions
          // Count withdrawal requests
          if (collection === 'withdrawalRequests') {
            withdrawalRequestsCount++;
          }
          firebaseLogger.debug('Withdrawal request excluded from volume', { collection, id, amount: amount.toFixed(2) });
        } else if (!isCompleted) {
          // Transaction is not completed (pending, failed, etc.) - don't count in volume
          skippedNotCompleted++;
          firebaseLogger.debug('Transaction not completed, excluded from volume', { 
            collection, 
            id, 
            status: txStatus,
            amount: amount.toFixed(2) 
          });
        } else if (!hasCompanyFee) {
          // Transaction has no company fees - platform didn't earn revenue, don't count in volume
          skippedNoFees++;
          firebaseLogger.debug('Transaction has no company fees, excluded from volume', { 
            collection, 
            id, 
            amount: amount.toFixed(2) 
          });
        } else if (txType.includes('input') || txType.includes('deposit') || txType.includes('funding')) {
          // Deposits are actual money coming in - count as volume (only if has fees and completed)
          totalInputVolume += amount;
          totalTransactionCount++;
          totalTransactionValue += amount; // Count in total value
        } else if (collection === 'moonpay_transactions' || txType.includes('moonpay')) {
          // MoonPay transactions are external money - count as volume (only if has fees and completed)
          moonpayVolume += amount;
          totalTransactionCount++;
          totalTransactionValue += amount; // Count in total value
        } else if (txType.includes('send') || txType.includes('transfer') || txType.includes('split')) {
          // Internal transfers/sends/splits are NOT volume - they're just moving money between users
          // Count the transaction but DON'T add to volume (it's already counted when deposited)
          totalSendCount++;
          // DO NOT add to totalTransactionCount or totalTransactionValue - this is internal movement, not new money
          firebaseLogger.debug('Internal transfer/split excluded from volume', { collection, id, amount: amount.toFixed(2) });
        } else {
          // Unknown type - only count if it looks like external money movement AND has fees AND is completed
          if (txType.includes('purchase') || txType.includes('payment') || txType.includes('external')) {
            totalTransactionCount++;
            totalTransactionValue += amount;
          } else {
            firebaseLogger.debug('Unknown transaction type excluded from volume', { 
              collection, 
              id, 
              type: txType,
              amount: amount.toFixed(2) 
            });
          }
        }
        
        // Debug logging for large transactions
        if (amount > 1000) {
          console.log(`💵 Large transaction: ${collection}/${id} - $${amount.toFixed(2)}`);
        }
        } else {
          // Amount is 0 or invalid, skip counting
          skippedInvalidAmounts++;
          if (amount > 50000) {
            console.warn(`⚠️ Skipping transaction with excessive amount: ${collection}/${id}, amount: $${amount}`);
          }
        }
        
        // Additional analytics from audit findings (run for all transactions in period)
        if (normalizedData.gas_fee) {
          totalGasFees += parseFloat(normalizedData.gas_fee);
        }
        if (normalizedData.company_fee) {
          totalCompanyFees += parseFloat(normalizedData.company_fee);
        }
        if (normalizedData.blockchain_network) {
          blockchainNetworks[normalizedData.blockchain_network] = (blockchainNetworks[normalizedData.blockchain_network] || 0) + 1;
        }
        if (normalizedData.device_info) {
          deviceInfo[normalizedData.device_info] = (deviceInfo[normalizedData.device_info] || 0) + 1;
        }
        if (normalizedData.app_version) {
          appVersions[normalizedData.app_version] = (appVersions[normalizedData.app_version] || 0) + 1;
        }
        if (normalizedData.transaction_method) {
          transactionMethods[normalizedData.transaction_method] = (transactionMethods[normalizedData.transaction_method] || 0) + 1;
        }
        if (normalizedData.gas_fee_covered_by_company === true) {
          gasFeeCoveredByCompany++;
      }
    });
    
    const averageTransactionValue = totalTransactionCount > 0 ? totalTransactionValue / totalTransactionCount : 0;
    
    // Debug logging with detailed breakdown
    firebaseLogger.debug('Transaction analytics complete', {
      summary: {
        totalDocumentsProcessed: allTransactionDocs.length,
        uniqueTransactions: processedTransactions.size,
        validTransactionsCounted: processedCount,
        transactionsInVolume: totalTransactionCount,
        transactionValue: totalTransactionValue.toFixed(2)
      },
      skipped: {
        testUsers: skippedTestUsers,
        duplicates: skippedDuplicates,
        invalidAmounts: skippedInvalidAmounts,
        outOfPeriod: skippedOutOfPeriod,
        devnet: skippedDevnet,
        noFees: skippedNoFees,
        notCompleted: skippedNotCompleted,
        requests: skippedRequests
      },
      financial: {
        totalTransactionValue: totalTransactionValue.toFixed(2),
        averageTransactionValue: averageTransactionValue.toFixed(2),
        requests: {
          count: totalRequestCount,
          volume: totalRequestVolume.toFixed(2)
        },
        sends: {
          count: totalSendCount,
          volume: totalSendVolume.toFixed(2)
        }
      }
    });
    
    return {
      totalTransactionCount,
      totalTransactionValue,
      averageTransactionValue,
      totalRequestCount,
      totalSendCount,
      totalRequestVolume,
      totalSendVolume,
      totalInputVolume,
      totalWithdrawalVolume,
      moonpayVolume,
      paymentRequestsCount,
      withdrawalRequestsCount,
      // New analytics from audit findings
      totalGasFees,
      totalCompanyFees,
      blockchainNetworks,
      deviceInfo,
      appVersions,
      transactionMethods,
      gasFeeCoveredByCompany,
      period
    };
  } catch (error) {
    console.error('Error fetching transaction analytics:', error);
    throw error;
  }
};

// Waitlist Analytics - Audited and Fixed
/**
 * Get waitlist analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Waitlist analytics data
 */
export const getWaitlistAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    // Get all waitlist entries and filter in memory due to date string format
    const waitlistRef = collection(db, 'Waitlist');
    const allWaitlistSnapshot = await getDocs(waitlistRef);
    
    let totalSubscribers = 0;
    let newSubscribers = 0;
    let emailSentSuccess = 0;
    let emailSentFailed = 0;
    let emailSentPending = 0;
    let emailErrors = 0;
    let verifiedSubscribers = 0;
    let pendingVerification = 0;
    
    
    if (allWaitlistSnapshot.size === 0) {
      // Return fallback data for empty collection
      const result = {
        totalSubscribers: 0,
        newSubscribers: 0,
        emailSentSuccess: 0,
        emailSentFailed: 0,
        emailSentPending: 0,
        emailErrors: 0,
        verifiedSubscribers: 0,
        pendingVerification: 0,
        verificationCount: 0,
        conversionRate: 0,
        emailSuccessRate: 0,
        averageWaitTime: 0,
        period,
        lastUpdated: new Date().toISOString()
      };
      return result;
    }
    
    allWaitlistSnapshot.forEach(doc => {
      const waitlistData = doc.data();
      
      // Filter out test users by email
      const email = waitlistData.email || waitlistData.userEmail || waitlistData.user_email;
      if (email && isTestUserEmail(email)) {
        return; // Skip this waitlist entry - belongs to test user
      }
      
      totalSubscribers++;
      
      // Check if subscriber was created in the period
      if (waitlistData.createdAt && isDateInPeriod(waitlistData.createdAt, startDate, endDate)) {
        newSubscribers++;
      }
      
      // Count email sending status
      if (waitlistData.emailSent === true) {
        emailSentSuccess++;
      } else if (waitlistData.emailSent === false) {
        emailSentFailed++;
      } else {
        emailSentPending++;
      }
      
      // Count email errors
      if (waitlistData.emailError) {
        emailErrors++;
      }
      
      // Count verification status based on email sending
      if (waitlistData.emailSentStatus === 'success' || waitlistData.emailSent === true) {
        verifiedSubscribers++;
      } else {
        pendingVerification++;
      }
    });
    
    // Get verification codes data for additional verification tracking
    let verificationCount = 0;
    try {
      const verificationRef = collection(db, 'VerificationCodes');
      const verificationSnapshot = await getDocs(verificationRef);
      
      verificationSnapshot.forEach(doc => {
        const verificationData = doc.data();
        if (verificationData.created_at && isDateInPeriod(verificationData.created_at, startDate, endDate)) {
          verificationCount++;
        }
      });
    } catch (verificationError) {
      console.warn('⚠️ Could not fetch verification codes data:', verificationError.message);
    }
    
    const conversionRate = totalSubscribers > 0 ? (verifiedSubscribers / totalSubscribers) * 100 : 0;
    const emailSuccessRate = totalSubscribers > 0 ? (emailSentSuccess / totalSubscribers) * 100 : 0;
    const averageWaitTime = 3.2; // This could be calculated from actual data
    
    const result = {
      totalSubscribers,
      newSubscribers,
      emailSentSuccess,
      emailSentFailed,
      emailSentPending,
      emailErrors,
      verifiedSubscribers,
      pendingVerification,
      verificationCount,
      conversionRate,
      emailSuccessRate,
      averageWaitTime,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching waitlist analytics:', error);
    throw error;
  }
};

// MoonPay Analytics
/**
 * Get MoonPay analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - MoonPay analytics data
 */
export const getMoonPayAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Helper: Validate and normalize financial amounts
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      const coerceToNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number' && isFinite(value)) return value;
        const s = String(value).replace(/[^0-9.+-]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
      };
      
      let normalized = coerceToNumber(amount);
      
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        const originalAmount = normalized;
        normalized = normalized / 100;
        console.log(`💰 ${context}: Converted cents to dollars: ${originalAmount} cents → $${normalized.toFixed(2)}`);
      }
      
      if (normalized < 0) {
        console.warn(`⚠️ ${context}: Negative amount: $${normalized}. Setting to 0.`);
        return 0;
      }
      if (normalized > maxAmount) {
        console.warn(`⚠️ ${context}: Suspiciously large amount: $${normalized.toFixed(2)}. Max: $${maxAmount}. Setting to 0.`);
        return 0;
      }
      
      return normalized;
    };
    
    
    const moonpayRef = collection(db, 'moonpay_transactions');
    const allMoonpaySnapshot = await getDocs(moonpayRef);
    
    let totalTransactions = 0;
    let totalVolume = 0;
    let successfulTransactions = 0;
    let failedTransactions = 0;
    let pendingTransactions = 0;
    let currencyBreakdown = {};
    let averageTransactionValue = 0;
    
    // Helper: robust number coercion
    const coerceToNumber = (value) => {
      if (value == null) return 0;
      if (typeof value === 'number' && isFinite(value)) return value;
      const s = String(value).replace(/[^0-9.+-]/g, '');
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    };
    
    // Track processed transactions to prevent double counting
    const processedTransactions = new Set();
    let skippedTestUsers = 0;
    let skippedDevnet = 0;
    let skippedInvalidAmounts = 0;
    let skippedDuplicates = 0;
    let skippedNoFees = 0;
    let skippedNotCompleted = 0;
    
    // Extract all unique user IDs from MoonPay transactions for batch checking
    const userIds = new Set();
    allMoonpaySnapshot.forEach(doc => {
      const moonpayData = doc.data();
      const userId = moonpayData.userId || moonpayData.user_id || moonpayData.customerId || moonpayData.customer_id;
      if (userId) userIds.add(userId);
    });
    
    // Batch check all user IDs for test users
    const testUserMap = await batchCheckTestUsers(Array.from(userIds));
    firebaseLogger.debug('MoonPay test user filter', { 
      checkedUsers: userIds.size, 
      testUsersFound: Array.from(testUserMap.values()).filter(Boolean).length 
    });
    
    allMoonpaySnapshot.forEach(doc => {
      const moonpayData = doc.data();
      const txId = doc.id;
      
      // Deduplication
      if (processedTransactions.has(txId)) {
        skippedDuplicates++;
        return;
      }
      
      // Check if MoonPay transaction belongs to a test user
      const userId = moonpayData.userId || moonpayData.user_id || moonpayData.customerId || moonpayData.customer_id;
      if (userId && testUserMap.get(userId)) {
        skippedTestUsers++;
        return; // Skip this MoonPay transaction - belongs to test user
      }
      
      // Also check email field if present
      const email = moonpayData.email || moonpayData.customerEmail || moonpayData.customer_email;
      if (email && isTestUserEmail(email)) {
        skippedTestUsers++;
        return; // Skip this MoonPay transaction - belongs to test user
      }
      
      // Filter out devnet transactions
      if (isDevnetTransaction(moonpayData)) {
        skippedDevnet++;
        return; // Skip devnet transactions
      }
      
      if (moonpayData.createdAt && isDateInPeriod(moonpayData.createdAt, startDate, endDate, `moonpay/${txId}`)) {
        const amount = validateAndNormalizeAmount(
          moonpayData.amount || moonpayData.value || 0,
          `moonpay/${txId}`,
          50000 // Lower max for MoonPay transactions
        );
        
        if (amount > 0 && amount <= 50000) {
          // Check transaction status
          const txStatus = (moonpayData.status || moonpayData.state || '').toLowerCase();
          const isCompleted = txStatus === 'completed' || 
                             txStatus === 'success' || 
                             txStatus === 'successful' || 
                             txStatus === 'settled' || 
                             txStatus === 'finished' ||
                             txStatus === 'executed';
          
          // Check if transaction has company fees (platform earned revenue)
          const companyFee = coerceToNumber(
            (moonpayData.company_fee ?? moonpayData.companyFee ?? moonpayData.fee?.company ?? moonpayData.fees?.company ??
            moonpayData.meta?.fees?.company ?? moonpayData.meta?.companyFee ?? moonpayData.tx?.fees?.company) ?? 0
          );
          const hasCompanyFee = companyFee > 0;
          
          // Track status for reporting (all transactions)
          if (isCompleted) {
          successfulTransactions++;
          } else if (txStatus === 'failed' || txStatus === 'error' || txStatus === 'cancelled') {
          failedTransactions++;
        } else {
          pendingTransactions++;
        }
          
          // IMPORTANT: Only count in volume if transaction is completed AND has company fees
          // This ensures we only count transactions where platform earned revenue and that actually occurred
          if (!isCompleted) {
            skippedNotCompleted++;
            firebaseLogger.debug('MoonPay transaction not completed, excluded from volume', { 
              id: txId, 
              status: txStatus,
              amount: amount.toFixed(2) 
            });
          } else if (!hasCompanyFee) {
            skippedNoFees++;
            firebaseLogger.debug('MoonPay transaction has no company fees, excluded from volume', { 
              id: txId, 
              amount: amount.toFixed(2) 
            });
          } else {
            // Mark as processed and count in volume
            processedTransactions.add(txId);
            totalTransactions++;
            totalVolume += amount;
        
        // Track currency
        const currency = moonpayData.currency || 'Unknown';
        currencyBreakdown[currency] = (currencyBreakdown[currency] || 0) + 1;
          }
        } else {
          skippedInvalidAmounts++;
        }
      }
    });
    
    firebaseLogger.debug('MoonPay Analytics Summary', {
      processed: {
        transactions: totalTransactions,
        volume: totalVolume.toFixed(2),
        successful: successfulTransactions,
        failed: failedTransactions,
        pending: pendingTransactions
      },
      skipped: {
        testUsers: skippedTestUsers,
        devnet: skippedDevnet,
        invalidAmounts: skippedInvalidAmounts,
        duplicates: skippedDuplicates,
        noFees: skippedNoFees,
        notCompleted: skippedNotCompleted
      }
    });
    
    averageTransactionValue = totalTransactions > 0 ? totalVolume / totalTransactions : 0;
    const successRate = totalTransactions > 0 ? (successfulTransactions / totalTransactions) * 100 : 0;
    
    const result = {
      totalTransactions,
      totalVolume,
      successfulTransactions,
      failedTransactions,
      pendingTransactions,
      averageTransactionValue,
      successRate,
      currencyBreakdown,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching MoonPay analytics:', error);
    throw error;
  }
};

// Payment Requests Analytics
/**
 * Get payment request analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Payment request analytics data
 */
export const getPaymentRequestAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Helper: Validate and normalize financial amounts
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      const coerceToNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number' && isFinite(value)) return value;
        const s = String(value).replace(/[^0-9.+-]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
      };
      
      let normalized = coerceToNumber(amount);
      
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        const originalAmount = normalized;
        normalized = normalized / 100;
        console.log(`💰 ${context}: Converted cents to dollars: ${originalAmount} cents → $${normalized.toFixed(2)}`);
      }
      
      if (normalized < 0) {
        console.warn(`⚠️ ${context}: Negative amount: $${normalized}. Setting to 0.`);
        return 0;
      }
      if (normalized > maxAmount) {
        console.warn(`⚠️ ${context}: Suspiciously large amount: $${normalized.toFixed(2)}. Max: $${maxAmount}. Setting to 0.`);
        return 0;
      }
      
      return normalized;
    };
    
    
    const paymentRequestsRef = collection(db, 'paymentRequests');
    const allPaymentRequestsSnapshot = await getDocs(paymentRequestsRef);
    
    // Extract all unique user IDs from payment requests for batch checking
    const userIds = new Set();
    allPaymentRequestsSnapshot.forEach(doc => {
      const requestData = doc.data();
      const userId = requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
      if (userId) userIds.add(userId);
    });
    
    // Batch check all user IDs for test users
    const testUserMap = await batchCheckTestUsers(Array.from(userIds));
    console.log(`[Test User Filter] Checked ${userIds.size} unique users for payment requests, found ${Array.from(testUserMap.values()).filter(Boolean).length} test users`);
    
    let totalRequests = 0;
    let totalAmount = 0;
    let approvedRequests = 0;
    let pendingRequests = 0;
    let rejectedRequests = 0;
    let averageRequestAmount = 0;
    let approvalRate = 0;
    
    // Track processed requests to prevent double counting
    const processedRequests = new Set();
    let skippedTestUsers = 0;
    let skippedDevnet = 0;
    let skippedInvalidAmounts = 0;
    let skippedDuplicates = 0;
    
    allPaymentRequestsSnapshot.forEach(doc => {
      const requestData = doc.data();
      const requestId = doc.id;
      
      // Deduplication
      if (processedRequests.has(requestId)) {
        skippedDuplicates++;
        return;
      }
      
      // Check if payment request belongs to a test user
      // Payment requests have fromUserId and toUserId (camelCase primary from mobile app)
      const fromUserId = requestData.fromUserId || requestData.from_user_id || requestData.userId || requestData.user_id || requestData.requesterId || requestData.requester_id;
      const toUserId = requestData.toUserId || requestData.to_user_id;
      // Check both sender and receiver
      if ((fromUserId && testUserMap.get(fromUserId)) || (toUserId && testUserMap.get(toUserId))) {
        skippedTestUsers++;
        return; // Skip this payment request - belongs to test user
      }
      
      // Also check email field if present
      const email = requestData.email || requestData.userEmail || requestData.user_email;
      if (email && isTestUserEmail(email)) {
        skippedTestUsers++;
        return; // Skip this payment request - belongs to test user
      }
      
      // Filter out devnet transactions
      if (isDevnetTransaction(requestData)) {
        skippedDevnet++;
        return; // Skip devnet requests
      }
      
      if (requestData.createdAt && isDateInPeriod(requestData.createdAt, startDate, endDate)) {
        const amount = validateAndNormalizeAmount(
          requestData.amount || requestData.value || 0,
          `paymentRequest/${requestId}`,
          50000 // Lower max for payment requests
        );
        
        if (amount > 0 && amount <= 50000) {
          // Mark as processed
          processedRequests.add(requestId);
          
          totalRequests++;
        totalAmount += amount;
        
        // Track status
        if (requestData.status === 'approved' || requestData.status === 'completed') {
          approvedRequests++;
        } else if (requestData.status === 'pending') {
          pendingRequests++;
        } else if (requestData.status === 'rejected' || requestData.status === 'cancelled') {
          rejectedRequests++;
          }
        } else {
          skippedInvalidAmounts++;
        }
      }
    });
    
    console.log(`📊 Payment Request Analytics Summary:`);
    console.log(`  ✅ Processed: ${totalRequests} requests, Total: $${totalAmount.toFixed(2)}`);
    console.log(`  ⏭️ Skipped - Test users: ${skippedTestUsers}, Devnet: ${skippedDevnet}, Invalid amounts: ${skippedInvalidAmounts}, Duplicates: ${skippedDuplicates}`);
    
    averageRequestAmount = totalRequests > 0 ? totalAmount / totalRequests : 0;
    approvalRate = totalRequests > 0 ? (approvedRequests / totalRequests) * 100 : 0;
    
    const result = {
      totalRequests,
      totalAmount,
      approvedRequests,
      pendingRequests,
      rejectedRequests,
      averageRequestAmount,
      approvalRate,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching payment request analytics:', error);
    throw error;
  }
};

// Notifications Analytics
/**
 * Get notification analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Notification analytics data
 */
export const getNotificationAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    const notificationsRef = collection(db, 'notifications');
    const allNotificationsSnapshot = await getDocs(notificationsRef);
    
    let totalNotifications = 0;
    let sentNotifications = 0;
    let deliveredNotifications = 0;
    let failedNotifications = 0;
    let notificationTypes = {};
    let deliveryRate = 0;
    
    
    allNotificationsSnapshot.forEach(doc => {
      const notificationData = doc.data();
      
      if (notificationData.createdAt && isDateInPeriod(notificationData.createdAt, startDate, endDate)) {
        totalNotifications++;
        
        // Track delivery status
        if (notificationData.delivered === true) {
          deliveredNotifications++;
        } else if (notificationData.sent === true) {
          sentNotifications++;
        } else if (notificationData.failed === true) {
          failedNotifications++;
        }
        
        // Track notification types
        const type = notificationData.type || 'unknown';
        notificationTypes[type] = (notificationTypes[type] || 0) + 1;
      }
    });
    
    deliveryRate = totalNotifications > 0 ? (deliveredNotifications / totalNotifications) * 100 : 0;
    
    const result = {
      totalNotifications,
      sentNotifications,
      deliveredNotifications,
      failedNotifications,
      deliveryRate,
      notificationTypes,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching notification analytics:', error);
    throw error;
  }
};

// Contact Analytics
/**
 * Get contact analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Contact analytics data
 */
export const getContactAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    const contactsRef = collection(db, 'contacts');
    const allContactsSnapshot = await getDocs(contactsRef);
    
    let totalContacts = 0;
    let newContacts = 0;
    let favoriteContacts = 0;
    let contactsWithWallets = 0;
    let totalMutualGroups = 0;
    let walletAddresses = {};
    let firstMetDates = {};
    
    
    allContactsSnapshot.forEach(doc => {
      const contactData = doc.data();
      totalContacts++;
      
      // Check if contact was created in the period
      if (contactData.created_at && isDateInPeriod(contactData.created_at, startDate, endDate)) {
        newContacts++;
      }
      
      // Count favorite contacts
      if (contactData.isFavorite === true) {
        favoriteContacts++;
      }
      
      // Count contacts with wallets
      if (contactData.wallet_address) {
        contactsWithWallets++;
        walletAddresses[contactData.wallet_address] = (walletAddresses[contactData.wallet_address] || 0) + 1;
      }
      
      // Count mutual groups
      if (contactData.mutual_groups_count) {
        totalMutualGroups += contactData.mutual_groups_count;
      }
      
      // Track first met dates
      if (contactData.first_met_at) {
        const date = new Date(contactData.first_met_at.seconds * 1000);
        const dateKey = date.toISOString().split('T')[0];
        firstMetDates[dateKey] = (firstMetDates[dateKey] || 0) + 1;
      }
    });
    
    const averageMutualGroups = totalContacts > 0 ? totalMutualGroups / totalContacts : 0;
    const walletAdoptionRate = totalContacts > 0 ? (contactsWithWallets / totalContacts) * 100 : 0;
    const favoriteRate = totalContacts > 0 ? (favoriteContacts / totalContacts) * 100 : 0;
    
    const result = {
      totalContacts,
      newContacts,
      favoriteContacts,
      contactsWithWallets,
      totalMutualGroups,
      averageMutualGroups,
      walletAdoptionRate,
      favoriteRate,
      walletAddresses,
      firstMetDates,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching contact analytics:', error);
    throw error;
  }
};

// Reminder Analytics
/**
 * Get reminder analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Reminder analytics data
 */
export const getReminderAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    const remindersRef = collection(db, 'reminders');
    const allRemindersSnapshot = await getDocs(remindersRef);
    
    let totalReminders = 0;
    let newReminders = 0;
    let totalReminderAmount = 0;
    let reminderTypes = {};
    let statusBreakdown = {};
    let groupBreakdown = {};
    
    
    allRemindersSnapshot.forEach(doc => {
      const reminderData = doc.data();
      totalReminders++;
      
      // Check if reminder was created in the period
      if (reminderData.created_at && isDateInPeriod(reminderData.created_at, startDate, endDate)) {
        newReminders++;
      }
      
      // Track reminder amounts
      if (reminderData.amount) {
        totalReminderAmount += reminderData.amount;
      }
      
      // Track reminder types
      if (reminderData.reminder_type) {
        reminderTypes[reminderData.reminder_type] = (reminderTypes[reminderData.reminder_type] || 0) + 1;
      }
      
      // Track status
      if (reminderData.status) {
        statusBreakdown[reminderData.status] = (statusBreakdown[reminderData.status] || 0) + 1;
      }
      
      // Track groups
      if (reminderData.group_id) {
        const groupId = reminderData.group_id.toString();
        groupBreakdown[groupId] = (groupBreakdown[groupId] || 0) + 1;
      }
    });
    
    const averageReminderAmount = totalReminders > 0 ? totalReminderAmount / totalReminders : 0;
    
    const result = {
      totalReminders,
      newReminders,
      totalReminderAmount,
      averageReminderAmount,
      reminderTypes,
      statusBreakdown,
      groupBreakdown,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching reminder analytics:', error);
    throw error;
  }
};

// Rate Limit Analytics
/**
 * Get rate limit analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Rate limit analytics data
 */
export const getRateLimitAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    
    const rateLimitsRef = collection(db, 'rateLimits');
    const allRateLimitsSnapshot = await getDocs(rateLimitsRef);
    
    let totalRateLimits = 0;
    let totalRequestCount = 0;
    let averageRequestCount = 0;
    let emailBreakdown = {};
    let lastRequestDates = {};
    
    
    allRateLimitsSnapshot.forEach(doc => {
      const rateLimitData = doc.data();
      totalRateLimits++;
      
      // Track request counts
      if (rateLimitData.requestCount) {
        totalRequestCount += rateLimitData.requestCount;
      }
      
      // Track emails
      if (rateLimitData.email) {
        emailBreakdown[rateLimitData.email] = (emailBreakdown[rateLimitData.email] || 0) + 1;
      }
      
      // Track last request dates
      if (rateLimitData.lastRequest) {
        const date = new Date(rateLimitData.lastRequest.seconds * 1000);
        const dateKey = date.toISOString().split('T')[0];
        lastRequestDates[dateKey] = (lastRequestDates[dateKey] || 0) + 1;
      }
    });
    
    averageRequestCount = totalRateLimits > 0 ? totalRequestCount / totalRateLimits : 0;
    
    const result = {
      totalRateLimits,
      totalRequestCount,
      averageRequestCount,
      emailBreakdown,
      lastRequestDates,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
    
  } catch (error) {
    console.error('❌ Error fetching rate limit analytics:', error);
    throw error;
  }
};

// Referral & Leaderboard Analytics
/**
 * Get referral analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Referral analytics data
 */
export const getReferralAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting referral analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Get all users and filter in memory (more reliable than Firestore queries with date strings)
    const usersRef = collection(db, 'users');
    const allUsersSnapshot = await getDocs(usersRef);
    
    let totalReferralCount = 0;
    let totalPointsDistributed = 0;
    const mostActiveUsers = [];
    let skippedTestUsers = 0;
    
    // Process all users
    allUsersSnapshot.forEach(doc => {
      const userData = doc.data();
      
      // Exclude test/development users from analytics
      if (isTestUser(userData)) {
        skippedTestUsers++;
        return; // Skip this user
      }
      
      const referralCount = userData.referralCount || 0;
      const points = userData.points || 0;
      
      // Only count referrals/points if user was active in the period
      // Check if user has any activity in the period (lastLoginAt, createdAt, or updatedAt)
      const lastLoginAt = userData.lastLoginAt || userData.last_login_at || userData.lastLogin;
      const createdAt = userData.createdAt || userData.created_at;
      const updatedAt = userData.updatedAt || userData.updated_at;
      
      const hasActivityInPeriod = 
        (lastLoginAt && isDateInPeriod(lastLoginAt, startDate, endDate)) ||
        (createdAt && isDateInPeriod(createdAt, startDate, endDate)) ||
        (updatedAt && isDateInPeriod(updatedAt, startDate, endDate));
      
      // Count all referrals and points (not just those in period, as referrals accumulate over time)
      // But only include users who were active in the period
      if (hasActivityInPeriod || referralCount > 0 || points > 0) {
      totalReferralCount += referralCount;
      totalPointsDistributed += points;
        
        // Only add to most active if they have referrals or points
        if (referralCount > 0 || points > 0) {
      mostActiveUsers.push({
        uid: doc.id,
        points: points,
        referralCount: referralCount,
        email: userData.primary_email || userData.email || 'N/A',
        name: userData.name || 'Anonymous',
            walletStatus: userData.wallet_status || 'unknown',
            lastLoginAt: lastLoginAt || null
      });
        }
      }
    });
    
    // Sort by points (descending), then by referral count
    mostActiveUsers.sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      return b.referralCount - a.referralCount;
    });
    
    // Take top 10
    const topUsers = mostActiveUsers.slice(0, 10);
    
    console.log(`📊 Referral Analytics Summary:`);
    console.log(`  ✅ Processed: ${allUsersSnapshot.size} users`);
    console.log(`  📈 Total referrals: ${totalReferralCount}, Total points: ${totalPointsDistributed}`);
    console.log(`  ⏭️ Skipped - Test users: ${skippedTestUsers}`);
    
    return {
      totalReferralCount,
      totalPointsDistributed,
      mostActiveUsers: topUsers,
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching referral analytics:', error);
    throw error;
  }
};

// Shared Wallet Analytics 🆕
/**
 * Get shared wallet analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Shared wallet analytics data
 */
export const getSharedWalletAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Helper: Validate and normalize financial amounts
    const validateAndNormalizeAmount = (amount, context = 'unknown', maxAmount = 50000) => {
      const coerceToNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number' && isFinite(value)) return value;
        const s = String(value).replace(/[^0-9.+-]/g, '');
        const n = parseFloat(s);
        return isNaN(n) ? 0 : n;
      };
      
      let normalized = coerceToNumber(amount);
      
      if (normalized > 1000 && normalized === Math.floor(normalized) && normalized < 1000000) {
        const originalAmount = normalized;
        normalized = normalized / 100;
        firebaseLogger.debug('Converted cents to dollars', { context, originalAmount, normalized: normalized.toFixed(2) });
      }
      
      if (normalized < 0) {
        firebaseLogger.warn('Negative amount found', { context, normalized });
        return 0;
      }
      if (normalized > maxAmount) {
        firebaseLogger.warn('Suspiciously large amount', { context, normalized: normalized.toFixed(2), maxAmount });
        return 0;
      }
      
      return normalized;
    };
    
    firebaseLogger.debug('Starting shared wallet analytics', { 
      period, 
      startDate: startDate.toISOString(), 
      endDate: endDate.toISOString() 
    });
    
    // Query shared wallets collection
    const sharedWalletsRef = collection(db, 'sharedWallets');
    const allWalletsSnapshot = await getDocs(sharedWalletsRef);
    
    // Query shared wallet transactions collection
    const sharedWalletTransactionsRef = collection(db, 'sharedWalletTransactions');
    const allTransactionsSnapshot = await getDocs(sharedWalletTransactionsRef);
    
    let totalWallets = 0;
    let newWallets = 0;
    let totalBalance = 0;
    let totalFundingVolume = 0;
    let totalWithdrawalVolume = 0;
    let activeWallets = 0;
    let walletsWithCards = 0;
    let totalMembers = 0;
    let averageMembersPerWallet = 0;
    let fundingCount = 0;
    let withdrawalCount = 0;
    
    // Extract user IDs for test user filtering
    const userIds = new Set();
    allWalletsSnapshot.forEach(doc => {
      const walletData = doc.data();
      if (walletData.creatorId) userIds.add(walletData.creatorId);
      if (walletData.members && Array.isArray(walletData.members)) {
        walletData.members.forEach(member => {
          if (member.userId) userIds.add(member.userId);
        });
      }
    });
    
    const testUserMap = await batchCheckTestUsers(Array.from(userIds));
    
    // Process shared wallets
    allWalletsSnapshot.forEach(doc => {
      const walletData = doc.data();
      
      // Check if wallet belongs to a test user
      if (walletData.creatorId && testUserMap.get(walletData.creatorId)) {
        return; // Skip test user wallets
      }
      
      totalWallets++;
      
      // Check if wallet was created in the period
      if (walletData.createdAt && isDateInPeriod(walletData.createdAt, startDate, endDate)) {
        newWallets++;
      }
      
      // Track balance - extract from shared wallet document (try multiple field names)
      const balance = validateAndNormalizeAmount(
        walletData.totalBalance || 
        walletData.balance || 
        walletData.currentBalance ||
        walletData.walletBalance ||
        0,
        `sharedWallet/${doc.id}/balance`,
        1000000 // Wallets can have larger balances
      );
      totalBalance += balance;
      
      if (balance > 0) {
        activeWallets++;
      }
      
      // Track members
      if (walletData.members && Array.isArray(walletData.members)) {
        totalMembers += walletData.members.length;
        
        // Check for linked cards
        const hasLinkedCards = walletData.members.some(member => 
          member.linkedCards && Array.isArray(member.linkedCards) && member.linkedCards.length > 0
        );
        if (hasLinkedCards) {
          walletsWithCards++;
        }
      }
      
      // Track updatedAt for activity
      if (walletData.updatedAt && isDateInPeriod(walletData.updatedAt, startDate, endDate)) {
        // Wallet was active in this period
      }
    });
    
    // Track processed transactions to prevent double counting
    const processedTransactions = new Set();
    let skippedTestUsers = 0;
    let skippedDevnet = 0;
    let skippedInvalidAmounts = 0;
    let skippedDuplicates = 0;
    
    // Process shared wallet transactions
    allTransactionsSnapshot.forEach(doc => {
      const txData = doc.data();
      const txId = doc.id;
      
      // Deduplication
      if (processedTransactions.has(txId)) {
        skippedDuplicates++;
        return;
      }
      
      // Check if transaction belongs to a test user
      if (txData.userId && testUserMap.get(txData.userId)) {
        skippedTestUsers++;
        return; // Skip test user transactions
      }
      
      // Filter out devnet transactions
      if (isDevnetTransaction(txData)) {
        skippedDevnet++;
        return; // Skip devnet transactions
      }
      
      // Check if transaction was created in the period
      if (txData.createdAt && isDateInPeriod(txData.createdAt, startDate, endDate)) {
        const amount = validateAndNormalizeAmount(
          txData.amount || 0,
          `walletTx/${txId}`,
          50000 // Lower max for wallet transactions
        );
        const txType = txData.type || 'unknown';
        
        if (amount > 0 && amount <= 50000) {
          // Mark as processed
          processedTransactions.add(txId);
        
        if (txType === 'funding') {
          fundingCount++;
          totalFundingVolume += amount;
        } else if (txType === 'withdrawal') {
          withdrawalCount++;
          totalWithdrawalVolume += amount;
        }
        } else {
          skippedInvalidAmounts++;
        }
      }
    });
    
    firebaseLogger.debug('Shared Wallet Analytics Summary', {
      wallets: {
        total: totalWallets,
        new: newWallets,
        active: activeWallets,
        withCards: walletsWithCards
      },
      balance: {
        total: totalBalance.toFixed(2),
        average: averageBalance.toFixed(2)
      },
      transactions: {
        funding: {
          count: fundingCount,
          volume: totalFundingVolume.toFixed(2)
        },
        withdrawals: {
          count: withdrawalCount,
          volume: totalWithdrawalVolume.toFixed(2)
        }
      },
      skipped: {
        testUsers: skippedTestUsers,
        devnet: skippedDevnet,
        invalidAmounts: skippedInvalidAmounts,
        duplicates: skippedDuplicates
      }
    });
    
    averageMembersPerWallet = totalWallets > 0 ? totalMembers / totalWallets : 0;
    const averageBalance = totalWallets > 0 ? totalBalance / totalWallets : 0;
    const fundingToWithdrawalRatio = totalWithdrawalVolume > 0 ? totalFundingVolume / totalWithdrawalVolume : 0;
    
    const result = {
      totalWallets,
      newWallets,
      totalBalance,
      averageBalance,
      totalFundingVolume,
      totalWithdrawalVolume,
      fundingToWithdrawalRatio,
      activeWallets,
      walletsWithCards,
      totalMembers,
      averageMembersPerWallet,
      fundingCount,
      withdrawalCount,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
  } catch (error) {
    firebaseLogger.error('Error fetching shared wallet analytics', { error });
    throw error;
  }
};

// Badge System Analytics 🆕
/**
 * Get badge system analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Badge analytics data
 */
export const getBadgeAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting badge analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Get all users to check badges
    const usersRef = collection(db, 'users');
    const allUsersSnapshot = await getDocs(usersRef);
    
    let totalBadgeClaims = 0;
    let usersWithBadges = 0;
    let usersWithActiveBadge = 0;
    let badgeDistribution = {};
    let totalPointsFromBadges = 0;
    let newBadgeClaims = 0;
    
    // Track badge claims from user badges subcollection
    const badgeClaimsByType = {
      achievement: 0,
      event: 0,
      community: 0
    };
    
    // Process users
    for (const userDoc of allUsersSnapshot.docs) {
      const userData = userDoc.data();
      
      // Exclude test users
      if (isTestUser(userData)) {
        continue;
      }
      
      // Check badges array in user document
      if (userData.badges && Array.isArray(userData.badges) && userData.badges.length > 0) {
        usersWithBadges++;
        totalBadgeClaims += userData.badges.length;
        
        // Track badge distribution
        userData.badges.forEach(badgeId => {
          badgeDistribution[badgeId] = (badgeDistribution[badgeId] || 0) + 1;
        });
      }
      
      // Check active badge
      if (userData.active_badge) {
        usersWithActiveBadge++;
      }
      
      // Badges are stored as an array in the user document, not a subcollection
      // Track badge claims from the badges array
      if (userData.badges && Array.isArray(userData.badges)) {
        // Count new badge claims (we can't determine claim date from array, so count all)
        // In a real scenario, you'd need to track claim dates separately
        newBadgeClaims += userData.badges.length;
        
        // Track badge distribution
        userData.badges.forEach(badgeId => {
          badgeDistribution[badgeId] = (badgeDistribution[badgeId] || 0) + 1;
        });
      }
      
      // Optional: Check user badges subcollection if it exists (for detailed claim data)
      // Note: Based on database check, this subcollection doesn't exist yet
      // Keeping this code for future use when subcollection is implemented
      try {
        const badgesSubcollectionRef = collection(db, 'users', userDoc.id, 'badges');
        const badgesSnapshot = await getDocs(badgesSubcollectionRef);
        
        if (badgesSnapshot.size > 0) {
          badgesSnapshot.forEach(badgeDoc => {
            const badgeData = badgeDoc.data();
            
            // Check if badge was claimed in the period
            if (badgeData.claimedAt && isDateInPeriod(badgeData.claimedAt, startDate, endDate)) {
              // Track points from badges
              if (badgeData.points) {
                totalPointsFromBadges += parseFloat(badgeData.points || 0);
              }
              
              // Track badge type if available
              const badgeType = (badgeData.type || 'achievement').toLowerCase();
              // Increment the count for the badge type, defaulting to 'achievement' if unknown
              if (badgeClaimsByType.hasOwnProperty(badgeType)) {
                badgeClaimsByType[badgeType]++;
              } else {
                // If type doesn't match known types, count as achievement
                badgeClaimsByType.achievement++;
              }
            }
          });
        }
      } catch (subcollectionError) {
        // Subcollection doesn't exist for most users, this is expected
        // Silently continue - badges are in the array field
      }
    }
    
    const averageBadgesPerUser = usersWithBadges > 0 ? totalBadgeClaims / usersWithBadges : 0;
    const activeBadgeRate = allUsersSnapshot.size > 0 ? (usersWithActiveBadge / allUsersSnapshot.size) * 100 : 0;
    const badgeAdoptionRate = allUsersSnapshot.size > 0 ? (usersWithBadges / allUsersSnapshot.size) * 100 : 0;
    
    // Get top badges
    const topBadges = Object.entries(badgeDistribution)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([badgeId, count]) => ({ badgeId, count }));
    
    const result = {
      totalBadgeClaims,
      newBadgeClaims,
      usersWithBadges,
      usersWithActiveBadge,
      averageBadgesPerUser,
      activeBadgeRate,
      badgeAdoptionRate,
      badgeDistribution,
      topBadges,
      badgeClaimsByType,
      totalPointsFromBadges,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching badge analytics:', error);
    throw error;
  }
};

// Phone Authentication Analytics 🆕
/**
 * Get phone authentication analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Phone auth analytics data
 */
export const getPhoneAuthAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    console.log('🚀 Starting phone auth analytics...');
    console.log(`📅 Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
    
    // Get all users
    const usersRef = collection(db, 'users');
    const allUsersSnapshot = await getDocs(usersRef);
    
    let totalUsers = 0;
    let usersWithPhone = 0;
    let usersWithVerifiedPhone = 0;
    let usersWithEmailAndPhone = 0;
    let newPhoneVerifications = 0;
    
    // Process users
    allUsersSnapshot.forEach(doc => {
      const userData = doc.data();
      
      // Exclude test users
      if (isTestUser(userData)) {
        return;
      }
      
      totalUsers++;
      
      // Check phone authentication
      if (userData.phone || userData.primary_phone) {
        usersWithPhone++;
        
        // Check if phone is verified
        if (userData.phoneVerified === true) {
          usersWithVerifiedPhone++;
          
          // Check if verification happened in this period
          // Note: We'd need a phoneVerifiedAt field for accurate tracking
          // For now, we'll estimate based on phoneVerified flag
        }
        
        // Check if user also has email (dual authentication)
        if (userData.email || userData.primary_email) {
          usersWithEmailAndPhone++;
        }
      }
    });
    
    // Check verification codes collection for new verifications
    try {
      // Try both collection name variations (VerificationCodes is primary from mobile app)
      const verificationCodesRef = collection(db, 'VerificationCodes');
      const verificationCodesSnapshot = await getDocs(verificationCodesRef);
      
      verificationCodesSnapshot.forEach(doc => {
        const codeData = doc.data();
        
        // Check if verification code was created in the period and used
        if (codeData.createdAt && isDateInPeriod(codeData.createdAt, startDate, endDate)) {
          if (codeData.used === true && codeData.phone) {
            newPhoneVerifications++;
          }
        }
      });
    } catch (verificationError) {
      console.log('⚠️ Could not fetch verification codes:', verificationError.message);
    }
    
    const phoneAuthAdoptionRate = totalUsers > 0 ? (usersWithPhone / totalUsers) * 100 : 0;
    const phoneVerificationRate = usersWithPhone > 0 ? (usersWithVerifiedPhone / usersWithPhone) * 100 : 0;
    const dualAuthRate = totalUsers > 0 ? (usersWithEmailAndPhone / totalUsers) * 100 : 0;
    
    const result = {
      totalUsers,
      usersWithPhone,
      usersWithVerifiedPhone,
      usersWithEmailAndPhone,
      newPhoneVerifications,
      phoneAuthAdoptionRate,
      phoneVerificationRate,
      dualAuthRate,
      period,
      lastUpdated: new Date().toISOString()
    };
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching phone auth analytics:', error);
    throw error;
  }
};

// Combined Analytics Dashboard Data
/**
 * Get comprehensive dashboard analytics for a given period
 * @param {string} [period='day'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Combined dashboard analytics data
 */
export const getDashboardAnalytics = async (period = FILTER_PERIODS.DAY) => {
  try {
    const [
      userAnalytics,
      sessionAnalytics,
      crashAnalytics,
      splitAnalytics,
      transactionAnalytics,
      referralAnalytics
    ] = await Promise.all([
      getUserAnalytics(period),
      getSessionAnalytics(period),
      getCrashAnalytics(period),
      getSplitAnalytics(period),
      getTransactionAnalytics(period),
      getReferralAnalytics(period)
    ]);
    
    return {
      users: userAnalytics,
      sessions: sessionAnalytics,
      crashes: crashAnalytics,
      splits: splitAnalytics,
      transactions: transactionAnalytics,
      referrals: referralAnalytics,
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching dashboard analytics:', error);
    throw error;
  }
};

// ============================================================================
// Communication Team Analytics Functions
// ============================================================================

/**
 * Get user segmentation analytics
 * Segments users by engagement level, lifecycle stage, feature usage, and value
 * Uses shared data processing utilities to avoid duplication
 * @param {string} [period='all'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - User segmentation data
 */
export const getUserSegmentationAnalytics = async (period = FILTER_PERIODS.ALL) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Check cache first
    let allUsersSnapshot = getCachedCollectionData('users', period);
    let splitsSnapshot = getCachedCollectionData('splits', period);
    let transactionsSnapshot = getCachedCollectionData('transactions', period);
    
    // Fetch if not cached
    if (!allUsersSnapshot) {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      allUsersSnapshot = snapshot.docs;
      setCachedCollectionData('users', period, allUsersSnapshot);
    }
    
    if (!splitsSnapshot) {
      const splitsRef = collection(db, 'splits');
      const snapshot = await getDocs(splitsRef);
      splitsSnapshot = snapshot.docs;
      setCachedCollectionData('splits', period, splitsSnapshot);
    }
    
    if (!transactionsSnapshot) {
      const transactionsRef = collection(db, 'transactions');
      const snapshot = await getDocs(transactionsRef);
      transactionsSnapshot = snapshot.docs;
      setCachedCollectionData('transactions', period, transactionsSnapshot);
    }
    
    // Process user data using shared utility
    const { allUsers } = processUserDataForAnalytics(
      allUsersSnapshot,
      splitsSnapshot,
      transactionsSnapshot,
      validateUserData,
      isTestUser
    );
    
    // Segment users using shared utilities
    const byEngagement = segmentUsersByEngagement(allUsers);
    const byLifecycle = segmentUsersByLifecycle(allUsers);
    const byFeatureUsage = segmentUsersByFeatureUsage(allUsers);
    const byValue = segmentUsersByValue(allUsers);
    
    const segments = {
      byEngagement,
      byLifecycle,
      byFeatureUsage,
      byValue
    };
    
    return {
      segments,
      summary: {
        totalUsers: allUsers.length,
        powerUsers: byEngagement.powerUsers.length,
        activeUsers: byEngagement.activeUsers.length,
        casualUsers: byEngagement.casualUsers.length,
        dormantUsers: byEngagement.dormantUsers.length,
        newUsers: byLifecycle.newUsers.length,
        atRiskUsers: byLifecycle.atRiskUsers.length,
        churnedUsers: byLifecycle.churnedUsers.length,
        splitCreators: byFeatureUsage.splitCreators.length,
        badgeEarners: byFeatureUsage.badgeEarners.length,
        walletUsers: byFeatureUsage.walletUsers.length,
        highValueUsers: byValue.highValue.length,
        mediumValueUsers: byValue.mediumValue.length,
        lowValueUsers: byValue.lowValue.length
      },
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching user segmentation analytics:', error);
    throw error;
  }
};

/**
 * Get user lifecycle analytics
 * Tracks user progression through signup → verification → onboarding → first activity → active user
 * Uses shared data processing utilities to avoid duplication
 * @param {string} [period='all'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - User lifecycle data
 */
export const getUserLifecycleAnalytics = async (period = FILTER_PERIODS.ALL) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Check cache first
    let allUsersSnapshot = getCachedCollectionData('users', period);
    let splitsSnapshot = getCachedCollectionData('splits', period);
    let transactionsSnapshot = getCachedCollectionData('transactions', period);
    
    // Fetch if not cached
    if (!allUsersSnapshot) {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      allUsersSnapshot = snapshot.docs;
      setCachedCollectionData('users', period, allUsersSnapshot);
    }
    
    if (!splitsSnapshot) {
      const splitsRef = collection(db, 'splits');
      const snapshot = await getDocs(splitsRef);
      splitsSnapshot = snapshot.docs;
      setCachedCollectionData('splits', period, splitsSnapshot);
    }
    
    if (!transactionsSnapshot) {
      const transactionsRef = collection(db, 'transactions');
      const snapshot = await getDocs(transactionsRef);
      transactionsSnapshot = snapshot.docs;
      setCachedCollectionData('transactions', period, transactionsSnapshot);
    }
    
    // Process user data using shared utility
    const { allUsers } = processUserDataForAnalytics(
      allUsersSnapshot,
      splitsSnapshot,
      transactionsSnapshot,
      validateUserData,
      isTestUser
    );
    
    const lifecycleStages = {
      newUsers: [], // 0-7 days since signup
      verified: [], // Email verified
      onboarded: [], // Completed onboarding
      firstActivity: [], // Created first split or transaction
      activeUsers: [], // Regular logins (last 7 days)
      powerUsers: [], // High feature usage
      atRisk: [], // Declining activity (7-30 days since last login)
      churned: [] // No activity 30+ days
    };
    
    let totalUsers = 0;
    let verifiedCount = 0;
    let onboardedCount = 0;
    let firstActivityCount = 0;
    let activeCount = 0;
    let churnedCount = 0;
    
    // Funnel data
    const funnel = {
      signup: 0,
      verified: 0,
      onboarded: 0,
      firstActivity: 0,
      active: 0
    };
    
    const now = new Date();
    
    allUsers.forEach(user => {
      totalUsers++;
      funnel.signup++;
      
      // Categorize by lifecycle stage
      if (user.daysSinceSignup !== null && user.daysSinceSignup <= 7) {
        lifecycleStages.newUsers.push(user);
      }
      
      if (user.isVerified) {
        lifecycleStages.verified.push(user);
        verifiedCount++;
        funnel.verified++;
      }
      
      if (user.isOnboarded) {
        lifecycleStages.onboarded.push(user);
        onboardedCount++;
        funnel.onboarded++;
      }
      
      if (user.firstActivityDate) {
        lifecycleStages.firstActivity.push(user);
        firstActivityCount++;
        funnel.firstActivity++;
      }
      
      if (user.daysSinceLastLogin !== null && user.daysSinceLastLogin <= 7) {
        lifecycleStages.activeUsers.push(user);
        activeCount++;
        funnel.active++;
      }
      
      if (user.daysSinceLastLogin !== null && user.daysSinceLastLogin > 7 && user.daysSinceLastLogin <= 30) {
        lifecycleStages.atRisk.push(user);
      }
      
      if (user.daysSinceLastLogin === null || user.daysSinceLastLogin > 30) {
        lifecycleStages.churned.push(user);
        churnedCount++;
      }
    });
    
    // Calculate conversion rates
    const conversionRates = {
      signupToVerified: totalUsers > 0 ? (verifiedCount / totalUsers) * 100 : 0,
      verifiedToOnboarded: verifiedCount > 0 ? (onboardedCount / verifiedCount) * 100 : 0,
      onboardedToFirstActivity: onboardedCount > 0 ? (firstActivityCount / onboardedCount) * 100 : 0,
      firstActivityToActive: firstActivityCount > 0 ? (activeCount / firstActivityCount) * 100 : 0
    };
    
    // Calculate average time to value
    const timeToValueValues = lifecycleStages.firstActivity
      .map(u => u.timeToValue)
      .filter(v => v !== null);
    const avgTimeToValue = timeToValueValues.length > 0
      ? timeToValueValues.reduce((sum, v) => sum + v, 0) / timeToValueValues.length
      : 0;
    
    // Re-engagement tracking (users who returned after inactivity)
    const reengagedUsers = lifecycleStages.churned.filter(user => {
      if (!user.lastLoginDate || !user.signupDate) return false;
      const daysSinceSignup = Math.floor((now - user.signupDate) / (1000 * 60 * 60 * 24));
      const daysSinceLastLogin = Math.floor((now - user.lastLoginDate) / (1000 * 60 * 60 * 24));
      // User was churned (30+ days inactive) but logged in recently (within last 7 days)
      return daysSinceSignup > 30 && daysSinceLastLogin <= 7;
    });
    
    return {
      lifecycleStages,
      funnel,
      conversionRates,
      summary: {
        totalUsers,
        newUsers: lifecycleStages.newUsers.length,
        verified: verifiedCount,
        onboarded: onboardedCount,
        firstActivity: firstActivityCount,
        active: activeCount,
        atRisk: lifecycleStages.atRisk.length,
        churned: churnedCount,
        reengaged: reengagedUsers.length
      },
      metrics: {
        avgTimeToValue: Math.round(avgTimeToValue * 10) / 10,
        churnRate: totalUsers > 0 ? (churnedCount / totalUsers) * 100 : 0,
        retentionRate: totalUsers > 0 ? ((totalUsers - churnedCount) / totalUsers) * 100 : 0
      },
      reengagedUsers,
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching user lifecycle analytics:', error);
    throw error;
  }
};

/**
 * Get communication channel analytics
 * Combines notification and waitlist data to measure communication effectiveness
 * @param {string} [period='all'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Communication channel analytics
 */
export const getCommunicationChannelAnalytics = async (period = FILTER_PERIODS.ALL) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Get notification analytics
    const notificationAnalytics = await getNotificationAnalytics(period).catch(() => null);
    
    // Get waitlist analytics
    const waitlistAnalytics = await getWaitlistAnalytics(period).catch(() => null);
    
    // Combine channel data
    const channels = {
      email: {
        totalSent: waitlistAnalytics?.emailSentSuccess || 0,
        totalFailed: waitlistAnalytics?.emailSentFailed || 0,
        totalPending: waitlistAnalytics?.emailSentPending || 0,
        successRate: waitlistAnalytics?.emailSuccessRate || 0,
        conversionRate: waitlistAnalytics?.conversionRate || 0,
        verifiedSubscribers: waitlistAnalytics?.verifiedSubscribers || 0,
        totalSubscribers: waitlistAnalytics?.totalSubscribers || 0
      },
      pushNotifications: {
        totalSent: notificationAnalytics?.sentNotifications || 0,
        totalDelivered: notificationAnalytics?.deliveredNotifications || 0,
        totalFailed: notificationAnalytics?.failedNotifications || 0,
        deliveryRate: notificationAnalytics?.deliveryRate || 0,
        types: notificationAnalytics?.notificationTypes || {}
      },
      inApp: {
        // In-app notifications would be tracked separately if available
        totalSent: 0,
        totalDelivered: 0,
        deliveryRate: 0
      }
    };
    
    // Calculate overall metrics
    const totalSent = channels.email.totalSent + channels.pushNotifications.totalSent + channels.inApp.totalSent;
    const totalDelivered = channels.email.totalSent + channels.pushNotifications.totalDelivered + channels.inApp.totalDelivered;
    const overallDeliveryRate = totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0;
    
    return {
      channels,
      summary: {
        totalSent,
        totalDelivered,
        overallDeliveryRate: Math.round(overallDeliveryRate * 10) / 10,
        emailSuccessRate: channels.email.successRate,
        pushDeliveryRate: channels.pushNotifications.deliveryRate
      },
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching communication channel analytics:', error);
    throw error;
  }
};

/**
 * Get user behavior analytics
 * Analyzes feature adoption, usage patterns, and user preferences
 * @param {string} [period='all'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - User behavior analytics
 */
export const getUserBehaviorAnalytics = async (period = FILTER_PERIODS.ALL) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Get existing analytics
    const [userAnalytics, splitAnalytics, badgeAnalytics, phoneAuthAnalytics, contactAnalytics] = await Promise.all([
      getUserAnalytics(period).catch(() => null),
      getSplitAnalytics(period).catch(() => null),
      getBadgeAnalytics(period).catch(() => null),
      getPhoneAuthAnalytics(period).catch(() => null),
      getContactAnalytics(period).catch(() => null)
    ]);
    
    // Feature adoption rates
    const totalUsers = userAnalytics?.totalUserCount || 0;
    const featureAdoption = {
      splits: {
        totalCreators: splitAnalytics?.mostActiveUsers?.length || 0,
        adoptionRate: totalUsers > 0 ? ((splitAnalytics?.mostActiveUsers?.length || 0) / totalUsers) * 100 : 0,
        avgSplitsPerCreator: splitAnalytics?.mostActiveUsers?.length > 0
          ? (splitAnalytics?.totalSplits || 0) / splitAnalytics?.mostActiveUsers?.length
          : 0
      },
      badges: {
        totalEarners: badgeAnalytics?.usersWithBadges || 0,
        adoptionRate: badgeAnalytics?.badgeAdoptionRate || 0,
        avgBadgesPerUser: badgeAnalytics?.averageBadgesPerUser || 0
      },
      wallets: {
        totalUsers: userAnalytics?.walletUsers || 0,
        adoptionRate: totalUsers > 0 ? ((userAnalytics?.walletUsers || 0) / totalUsers) * 100 : 0
      },
      phoneAuth: {
        totalUsers: phoneAuthAnalytics?.usersWithPhone || 0,
        adoptionRate: phoneAuthAnalytics?.phoneAuthAdoptionRate || 0
      },
      sharedWallets: {
        // Would need shared wallet analytics
        totalUsers: 0,
        adoptionRate: 0
      }
    };
    
    // Usage patterns (from split analytics)
    const usagePatterns = {
      splitTypes: {
        fair: splitAnalytics?.fairSplits || 0,
        degen: splitAnalytics?.degenSplits || 0,
        fairPercentage: splitAnalytics?.totalSplits > 0
          ? ((splitAnalytics?.fairSplits || 0) / splitAnalytics?.totalSplits) * 100
          : 0,
        degenPercentage: splitAnalytics?.totalSplits > 0
          ? ((splitAnalytics?.degenSplits || 0) / splitAnalytics?.totalSplits) * 100
          : 0
      },
      avgParticipants: splitAnalytics?.averageParticipants || 0,
      avgSplitValue: splitAnalytics?.averageSplitAmount || 0
    };
    
    // Preferences (from badge and split data)
    const preferences = {
      preferredSplitType: splitAnalytics?.fairSplits > splitAnalytics?.degenSplits ? 'fair' : 'degen',
      topBadges: badgeAnalytics?.topBadges || [],
      badgeDistribution: badgeAnalytics?.badgeDistribution || {}
    };
    
    // Social graph (from contacts)
    const socialGraph = {
      totalContacts: contactAnalytics?.totalContacts || 0,
      avgContactsPerUser: totalUsers > 0 ? ((contactAnalytics?.totalContacts || 0) / totalUsers) : 0,
      favoriteContacts: contactAnalytics?.favoriteContacts || 0,
      contactsWithWallets: contactAnalytics?.contactsWithWallets || 0,
      avgMutualGroups: contactAnalytics?.averageMutualGroups || 0
    };
    
    return {
      featureAdoption,
      usagePatterns,
      preferences,
      socialGraph,
      summary: {
        totalUsers,
        splitCreators: featureAdoption.splits.totalCreators,
        badgeEarners: featureAdoption.badges.totalEarners,
        walletUsers: featureAdoption.wallets.totalUsers,
        phoneAuthUsers: featureAdoption.phoneAuth.totalUsers
      },
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching user behavior analytics:', error);
    throw error;
  }
};

/**
 * Get cohort analytics
 * Tracks user groups by signup date and measures retention over time
 * Uses cached data to avoid duplicate fetching
 * @param {string} [period='all'] - Time period (from FILTER_PERIODS)
 * @returns {Promise<Object>} - Cohort analytics data
 */
export const getCohortAnalytics = async (period = FILTER_PERIODS.ALL) => {
  try {
    const { startDate, endDate } = getDateRange(period);
    
    // Check cache first
    let allUsersSnapshot = getCachedCollectionData('users', period);
    let splitsSnapshot = getCachedCollectionData('splits', period);
    
    // Fetch if not cached
    if (!allUsersSnapshot) {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      allUsersSnapshot = snapshot.docs;
      setCachedCollectionData('users', period, allUsersSnapshot);
    }
    
    if (!splitsSnapshot) {
      const splitsRef = collection(db, 'splits');
      const snapshot = await getDocs(splitsRef);
      splitsSnapshot = snapshot.docs;
      setCachedCollectionData('splits', period, splitsSnapshot);
    }
    
    // Build user activity map
    const userSignupDates = new Map();
    const userActivityByMonth = new Map();
    
    // Process users
    allUsersSnapshot.forEach(doc => {
      const rawUserData = doc.data();
      
      // Exclude test users
      if (isTestUser(rawUserData)) {
        return;
      }
      
      const userData = validateUserData(rawUserData);
      const userId = doc.id;
      
      if (userData.createdAt) {
        const signupDate = new Date(userData.createdAt);
        const signupMonth = `${signupDate.getFullYear()}-${String(signupDate.getMonth() + 1).padStart(2, '0')}`;
        userSignupDates.set(userId, signupMonth);
        
        // Initialize activity tracking
        if (!userActivityByMonth.has(userId)) {
          userActivityByMonth.set(userId, new Set());
        }
      }
    });
    
    // Process splits to track monthly activity
    splitsSnapshot.forEach(doc => {
      const splitData = doc.data();
      const userId = splitData.creatorId || splitData.userId || splitData.creator_id || splitData.user_id;
      
      if (userId && userSignupDates.has(userId)) {
        const createdAt = splitData.createdAt || splitData.created_at || splitData.timestamp;
        if (createdAt) {
          const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
          const activityMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          userActivityByMonth.get(userId).add(activityMonth);
        }
      }
    });
    
    // Build cohorts by signup month
    const cohorts = new Map();
    
    userSignupDates.forEach((signupMonth, userId) => {
      if (!cohorts.has(signupMonth)) {
        cohorts.set(signupMonth, {
          signupMonth,
          totalUsers: 0,
          activeUsers: new Set(),
          retentionByMonth: new Map()
        });
      }
      
      const cohort = cohorts.get(signupMonth);
      cohort.totalUsers++;
      
      // Track activity by month
      const activityMonths = userActivityByMonth.get(userId) || new Set();
      activityMonths.forEach(month => {
        cohort.activeUsers.add(userId);
        
        // Calculate months since signup
        const signupDate = new Date(signupMonth + '-01');
        const activityDate = new Date(month + '-01');
        const monthsSinceSignup = (activityDate.getFullYear() - signupDate.getFullYear()) * 12 +
          (activityDate.getMonth() - signupDate.getMonth());
        
        if (!cohort.retentionByMonth.has(monthsSinceSignup)) {
          cohort.retentionByMonth.set(monthsSinceSignup, new Set());
        }
        cohort.retentionByMonth.get(monthsSinceSignup).add(userId);
      });
    });
    
    // Convert to array format
    const cohortData = Array.from(cohorts.values()).map(cohort => {
      const retentionRates = new Map();
      cohort.retentionByMonth.forEach((users, month) => {
        retentionRates.set(month, (users.size / cohort.totalUsers) * 100);
      });
      
      return {
        signupMonth: cohort.signupMonth,
        totalUsers: cohort.totalUsers,
        activeUsers: cohort.activeUsers.size,
        retentionRates: Object.fromEntries(retentionRates)
      };
    }).sort((a, b) => a.signupMonth.localeCompare(b.signupMonth));
    
    // Calculate overall retention metrics
    let totalCohortUsers = 0;
    let totalActiveUsers = 0;
    
    cohortData.forEach(cohort => {
      totalCohortUsers += cohort.totalUsers;
      totalActiveUsers += cohort.activeUsers;
    });
    
    const overallRetentionRate = totalCohortUsers > 0
      ? (totalActiveUsers / totalCohortUsers) * 100
      : 0;
    
    return {
      cohorts: cohortData,
      summary: {
        totalCohorts: cohortData.length,
        totalUsers: totalCohortUsers,
        totalActiveUsers,
        overallRetentionRate: Math.round(overallRetentionRate * 10) / 10
      },
      period,
      lastUpdated: new Date().toISOString()
    };
  } catch (error) {
    console.error('❌ Error fetching cohort analytics:', error);
    throw error;
  }
};

// ============================================================================
// Real-Time Analytics Subscriptions
// ============================================================================

/**
 * Subscribe to user analytics with real-time updates
 * Uses onSnapshot instead of getDocs for live data
 * @param {string} period - Time period (from FILTER_PERIODS)
 * @param {string|null} platform - Platform filter
 * @param {Function} callback - Callback function (data, error) => void
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUserAnalytics = (period = FILTER_PERIODS.DAY, platform = null, callback) => {
  return subscribeToCollection(
    'users',
    async (users, error) => {
      if (error) {
        callback(null, error);
        return;
      }

      try {
        // Process users using existing logic
        const { startDate, endDate } = getDateRange(period);
        const processedData = await processUserDataForAnalytics(
          users.map(u => ({ id: u.id, data: () => u })),
          [],
          [],
          validateUserData,
          isTestUser
        );

        // Calculate analytics (simplified version)
        const analytics = {
          totalUserCount: processedData.allUsers.length,
          periodUserCount: processedData.allUsers.filter(u => 
            u.createdAt && isDateInPeriod(u.createdAt, startDate, endDate)
          ).length,
          verifiedUsers: processedData.allUsers.filter(u => u.emailVerified).length,
          onboardedUsers: processedData.allUsers.filter(u => u.hasCompletedOnboarding).length,
          walletUsers: processedData.allUsers.filter(u => u.walletAddress).length,
          period,
          lastUpdated: new Date().toISOString()
        };

        callback(analytics, null);
      } catch (err) {
        callback(null, err);
      }
    },
    {
      transform: (data) => data.map(doc => ({ id: doc.id, ...doc }))
    }
  );
};

/**
 * Subscribe to split analytics with real-time updates
 * @param {string} period - Time period
 * @param {Function} callback - Callback function
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToSplitAnalytics = (period = FILTER_PERIODS.DAY, callback) => {
  const { startDate, endDate } = getDateRange(period);
  
  return subscribeToDateRange(
    'splits',
    startDate,
    endDate,
    'created_at',
    async (splits, error) => {
      if (error) {
        callback(null, error);
        return;
      }

      try {
        // Process splits using existing logic
        const analytics = await getSplitAnalytics(period);
        callback(analytics, null);
      } catch (err) {
        callback(null, err);
      }
    }
  );
};

/**
 * Subscribe to transaction analytics with real-time updates
 * @param {string} period - Time period
 * @param {Function} callback - Callback function
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToTransactionAnalytics = (period = FILTER_PERIODS.DAY, callback) => {
  const { startDate, endDate } = getDateRange(period);
  
  return subscribeToDateRange(
    'transactions',
    startDate,
    endDate,
    'created_at',
    async (transactions, error) => {
      if (error) {
        callback(null, error);
        return;
      }

      try {
        // Process transactions using existing logic
        const analytics = await getTransactionAnalytics(period);
        callback(analytics, null);
      } catch (err) {
        callback(null, err);
      }
    }
  );
};

/**
 * Subscribe to waitlist with real-time updates
 * @param {Function} callback - Callback function
 * @param {Object} options - Query options { limit }
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToWaitlist = (callback, options = {}) => {
  return subscribeToCollection(
    'Waitlist',
    (entries, error) => {
      if (error) {
        callback(null, error);
        return;
      }

      const sorted = entries.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || new Date(a.timestamp || 0);
        const bTime = b.timestamp?.toDate?.() || new Date(b.timestamp || 0);
        return bTime - aTime;
      });

      const limited = options.limit ? sorted.slice(0, options.limit) : sorted;
      callback(limited, null);
    },
    {
      orderByField: 'timestamp',
      orderDirection: 'desc',
      limitCount: options.limit || null
    }
  );
};
